/* =============================================================
   LATB — Typesense Collection & Search Page Client
   Loads product data from Typesense, renders grid, handles
   filters, sort, infinite scroll, lazy loading.

   Config comes from window.LATB_CFG (set by the Liquid section).
   ============================================================= */

(function () {
  'use strict';

  const CFG = window.LATB_CFG;
  if (!CFG) return;

  // ---------- DOM refs ----------
  const root = document.querySelector('[data-lts-root]');
  if (!root) return;

  const el = {
    root,
    count: root.querySelector('[data-lts-count]'),
    sort: root.querySelector('[data-lts-sort]'),
    sorts: root.querySelectorAll('[data-lts-sort]'),
    sortDisplay: root.querySelector('[data-lts-sort-display]'),
    sortDisplays: root.querySelectorAll('[data-lts-sort-display]'),
    sizeRow: root.querySelector('[data-lts-facet="sizes"]'),
    catRow: root.querySelector('[data-lts-facet="product_type"]'),
    // Mobile drawer mirrors
    sizeRowMobile: root.querySelector('[data-lts-facet-mobile="sizes"]'),
    catAllMobile: root.querySelector('[data-lts-cat-group-mobile="all"]'),
    activeChips: root.querySelector('[data-lts-active-filters]'),
    grid: root.querySelector('[data-lts-grid]'),
    loader: root.querySelector('[data-lts-loader]'),
    empty: root.querySelector('[data-lts-empty]'),
    sentinel: root.querySelector('[data-lts-sentinel]'),
    clearAll: root.querySelector('[data-lts-clear-all]'),
    // Mobile drawer
    drawer: root.querySelector('[data-lts-drawer]'),
    drawerOpenBtn: root.querySelector('[data-lts-open-drawer]'),
    drawerCloseBtns: root.querySelectorAll('[data-lts-close-drawer]')
  };

  // ---------- State ----------
  // Search results default to text-match relevance (best-matching first);
  // collection pages default to Shopify's merchant-set "Featured" order.
  const DEFAULT_SORT = (window.LATB_CFG && window.LATB_CFG.isSearchPage)
    ? '_text_match:desc,published_at:desc'
    : 'published_at:desc';
  // "Best selling" is not a Typesense field. Its order comes from Shopify
  // (Storefront API, or templates/collection.lts-order.liquid as a backup);
  // see searchBestSelling.
  const BEST_SELLING_SORT = 'best-selling';

  const state = {
    query: getInitialSearchQuery(),
    sort: DEFAULT_SORT,
    selected: {
      sizes: new Set(),
      product_type: new Set()
    },
    page: 1,
    total: Number(CFG.initialCount) || 0,
    loading: false,
    exhausted: false,
    facetsInitialized: false,
    usingTagFallback: false,
    usingMerchantOrder: false,
    prefetchedPage: null,  // in-flight next page cached for instant swap
    listGen: 0,            // +1 on every reset; a prefetch from an older list is thrown away
    bestSellingCache: null,   // collection ids in Shopify's best-selling order (see bestSellingCacheFor)
    bestFilteredCache: null,  // that order narrowed to the current filters (see searchBestSelling)
    bestSellingFallback: false, // true while a best-selling list is being shown in the default sort
    // Cache the set of facet values that exist in this collection with NO
    // user filters applied. This freezes the "clickable" set so multi-select
    // works even after one selection narrows the current result set.
    availableSizes: null,     // Set<string> once initialized
    availableCategories: null // Set<string> once initialized
  };
  window.__latbState = state;

  // ---------- Fixed categories ----------
  // 14 categories in the exact display order from the live screenshot,
  // grouped as "kids" (left of divider) and "adults" (right of divider).
  // Each category maps to product_type values that trigger it (case-insensitive
  // substring match on Typesense's product_type facet).
  const CATEGORIES = {
    kids: [
      { key: 'dresses',    label: 'Dresses',      matches: ['dress'] },
      { key: 'sets',       label: 'Sets',         matches: ['set'] },
      { key: 'tops',       label: 'Tops',         matches: ['top', 'shirt', 'tee', 'blouse'] },
      { key: 'bottoms',    label: 'Bottoms',      matches: ['bottom', 'pant', 'short', 'legging', 'jean', 'skirt'] },
      { key: 'accessories',label: 'Accessories',  matches: ['accessor', 'bag', 'backpack', 'sunglass', 'hat', 'jewel', 'hair'] },
      { key: 'shoes',      label: 'Shoes',        matches: ['shoe', 'sneaker', 'boot', 'slipper', 'slide', 'sandal', 'footwear'] }
    ],
    adults: [
      { key: 'sweaters',   label: 'Sweaters',     matches: ['sweater', 'sweatshirt', 'hoodie', 'cardigan'] },
      { key: 'outerwear',  label: 'Outerwear',    matches: ['outer', 'coat', 'bomber', 'jacket'] },
      { key: 'swim',       label: 'Swim',         matches: ['swim', 'bikini', 'bathing'] },
      { key: 'rompers',    label: 'Rompers',      matches: ['romper', 'jumpsuit', 'onesie', 'overall'] },
      { key: 'baby',       label: 'Baby & Toddler', matches: ['baby', 'toddler', 'infant'] },
      { key: 'women',      label: "Women's",      matches: ['women'] },
      { key: 'toys',       label: 'Toys',         matches: ['toy', 'plush'] },
      { key: 'baskets',    label: 'Baskets',      matches: ['basket'] },
      { key: 'sale',       label: 'Sale',         matches: [], isSale: true }
    ]
  };

  // ---------- Category icons ----------
  const FALLBACK_SVG = '<svg viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="1.7"><rect x="13" y="13" width="22" height="22" rx="3"/></svg>';

  // Icons are keyed to CATEGORIES entries by their `key` field.
  const CATEGORY_ICONS = {
    dresses: "<svg id=\"Layer_1\" data-name=\"Layer 1\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 1090 1080\"> <defs> <style> .cls-1 { fill: none; stroke: currentColor; stroke-linecap: round; stroke-width: 18px; } </style> </defs> <g id=\"dress\"> <path class=\"cls-1\" d=\"M674.91,481.29h-259.81l-61.5-177.58c-1.67-8.33,4.99-15.75,13.48-15.45,57.4,2.02,106.86-35.76,177.92,16.18,71.06-51.95,120.52-14.17,177.92-16.18,8.49-.3,15.15,7.12,13.48,15.45l-61.5,177.58Z\"/> <path class=\"cls-1\" d=\"M674.65,481.29c78.62,95.32,142.9,311.77,171.19,389.89.01,52.39-601.7,52.39-601.69,0,28.3-78.12,92.57-294.57,171.19-389.89h259.31Z\"/> <line class=\"cls-1\" x1=\"428.87\" y1=\"169.52\" x2=\"389.11\" y2=\"279.94\"/> <line class=\"cls-1\" x1=\"661.92\" y1=\"169.52\" x2=\"701.67\" y2=\"279.94\"/> </g> </svg>",
    sets: "<svg id=\"Layer_1\" data-name=\"Layer 1\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 1090 1080\"> <defs> <style> .cls-1 { fill: currentColor; fill-rule: evenodd; } </style> </defs> <path class=\"cls-1\" d=\"M540.41,619.39l-4.59,2.75-4.59,8.26.92,8.26,4.59,5.51,3.67,1.84,11.01-.92,6.42-6.42.92-2.75-.92-8.26-1.84-3.67-6.42-4.59h-9.18ZM357.77,598.28l-2.75,7.34-7.34,40.38-13.77,38.55-61.49,219.35.92,9.18,4.59,6.42,4.59,2.75,28.45,9.18,83.52,23.86,73.42,17.44,36.71,7.34h5.51l7.34-3.67,3.67-5.51,21.11-65.16,1.84-2.75h1.84l2.75,5.51v2.75l21.11,62.41,7.34,5.51,8.26.92,68.83-14.68,61.49-15.6,91.78-27.53,5.51-3.67,4.59-9.18-.92-9.18-63.33-226.69-10.1-27.53-8.26-42.22-2.75-5.51-4.59-4.59-5.51-2.75h-354.26l-5.51,2.75-4.59,4.59ZM570.7,694.64l3.67.92,11.93,7.34,13.77,12.85,4.59,9.18v5.51l-2.75,4.59-1.84.92-4.59-.92-9.18-9.18-11.01-17.44-5.51-11.01.92-2.75ZM519.3,694.64l.92,3.67-8.26,15.6-9.18,13.77-6.42,6.42-3.67,1.84-3.67-.92-2.75-3.67v-8.26l4.59-8.26,11.93-11.01,14.68-9.18h1.84ZM289.86,902.98l7.34-22.94.92-6.42,7.34-22.94,8.26-33.04,37.63-133.08,9.18-26.62,1.84-1.84,20.19,4.59,51.4,7.34,28.45,2.75,29.37.92.92.92h24.78l.92.92-.92,2.75-8.26,3.67-14.68,9.18-15.6,13.77-4.59,6.42-4.59,11.01-.92,5.51.92,9.18,3.67,8.26,8.26,7.34,6.42,1.84h6.42l8.26-2.75,6.42-4.59,9.18-10.1,14.68-25.7,2.75-.92.92.92v81.68l3.67,4.59,2.75.92,6.42-.92,3.67-4.59v-81.68l.92-.92,2.75.92,7.34,13.77,11.93,17.44,8.26,7.34,7.34,3.67,11.01.92,7.34-2.75,7.34-7.34,3.67-11.01-.92-11.93-8.26-15.6-17.44-15.6-20.19-11.93.92-2.75,52.31-2.75,36.71-3.67,64.24-11.01,1.84,1.84,7.34,22.03,62.41,225.77-1.84,1.84-29.37,9.18-84.43,23.86-57.82,13.77-42.22,8.26-1.84-2.75-26.62-81.68-4.59-5.51-8.26-1.84-4.59,1.84-4.59,5.51-27.53,83.52-2.75.92-60.57-12.85-71.59-18.36-79.85-23.86-1.84-1.84v-2.75ZM366.03,635.91l5.51-26.62,1.84-1.84h343.25l2.75,3.67,5.51,27.53-3.67,1.84-22.94,4.59-55.98,7.34-33.04,1.84-.92.92h-17.44l-.92.92-60.57.92-.92-.92h-28.45l-.92-.92-29.37-.92-45.89-4.59-50.48-8.26-6.42-1.84-.92-3.67ZM704.69,104.52l-11.93-4.59h-8.26l-10.1,3.67-5.51,5.51-4.59,14.68-.92,10.1-16.52,89.02-11.01,17.44-17.44,17.44-25.7,17.44-21.11,9.18-22.03,4.59h-8.26l-15.6-2.75-24.78-9.18-22.03-13.77-20.19-18.36-10.1-12.85-5.51-11.01-7.34-44.05-5.51-24.78-.92-10.1-6.42-29.37-4.59-6.42-6.42-3.67-6.42-1.84-13.77.92-6.42,2.75-5.51,4.59-4.59,7.34-4.59,19.27-9.18,61.49-.92,47.72.92.92,1.84,37.63-12.85,10.1-22.94,11.01-3.67,5.51,1.84,21.11,7.34,48.64,4.59,44.05,1.84,7.34,5.51,50.48,5.51,36.71v8.26l1.84,8.26,3.67,5.51,6.42,3.67h366.19l3.67-1.84,5.51-5.51,1.84-5.51,27.53-220.26-1.84-4.59-2.75-2.75-6.42-1.84-12.85-6.42-17.44-12.85,2.75-26.62v-52.31l-1.84-18.36-11.01-64.24-3.67-9.18-7.34-7.34ZM706.53,185.28l3.67,1.84v9.18l1.84,10.1v50.48l-2.75.92-2.75-3.67-4.59-20.19v-22.94l4.59-25.7ZM382.55,181.61l1.84,3.67,2.75,17.44.92,31.2-4.59,20.19-1.84,4.59-2.75.92-1.84-11.01v-37.63l2.75-28.45,2.75-.92ZM694.6,118.28l2.75,2.75.92,3.67-.92,15.6-11.93,69.75v25.7l2.75,15.6,4.59,13.77,12.85,21.11,16.52,15.6,15.6,10.1,10.1,3.67,1.84,1.84-26.62,209.25-.92.92h-353.34l-1.84-4.59-18.36-154.19-5.51-35.79-.92-15.6,23.86-13.77,18.36-17.44,8.26-11.93,8.26-20.19,3.67-18.36v-24.78l-3.67-27.53-7.34-36.71-.92-8.26.92-17.44,3.67-2.75h8.26l2.75,3.67,19.27,105.54,4.59,10.1,11.93,16.52,25.7,23.86,25.7,15.6,29.37,10.1,11.01,1.84h19.27l28.45-7.34,27.53-13.77,22.94-17.44,9.18-9.18,11.01-13.77,9.18-18.36,14.68-78.01.92-10.1,2.75-10.1v-5.51l3.67-4.59,9.18.92Z\"/> </svg>",
    tops: "<svg id=\"Layer_1\" data-name=\"Layer 1\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 1090 1080\"> <defs> <style> .cls-1 { fill: currentColor; } </style> </defs> <path class=\"cls-1\" d=\"M563.03,819.43c-65.3.68-113.4,1.31-161.5,1.63-26.49.18-25.15.81-25.32-25.85-.4-64.13-.03-128.27.04-192.41.02-22.12.11-44.23.1-66.35,0-3.85-.09-7.75-.67-11.54-.19-1.25-2.12-3.28-3.1-3.2-9.57.8-16.72-5.23-24.68-8.67-21.8-9.42-43.42-19.28-64.99-29.23-17.06-7.86-16.46-8.85-11.52-24.69,9.2-29.53,17.5-59.32,29.99-87.72,5.57-12.67,11.96-24.87,19.1-36.73,25.46-42.31,63.5-64.89,111.72-71.6,18.09-2.52,36.25-3.7,54.54-3.69,53.08.04,106.16-.3,159.23-.47,30.88-.1,59.1,8.77,83.58,27.35,20.36,15.46,37.59,34.09,49.95,56.91,12.98,23.97,21.76,49.53,29.31,75.54,4.46,15.37,7.86,31.04,12.14,46.47,1.77,6.37.05,10.83-5.32,14.1-3.29,2.01-6.72,3.83-10.19,5.5-25.4,12.19-50.65,24.73-76.35,36.24-8.62,3.86-10.95,9.1-10.91,17.87.35,64.69.32,129.38.42,194.07.03,21.56.19,43.13.11,64.69-.07,19.62-2.38,21.79-21.96,21.78-50.31,0-100.63,0-133.73,0ZM700.13,625.07h-.13c0-53.08,0-106.16,0-159.23,0-4.42-.11-8.86.21-13.26.35-4.9,3.5-7.91,8.44-8.67,4.03-.62,8.85,2.39,9.96,6.96.89,3.71,1.1,7.64,1.16,11.49.2,12.16.11,24.33.3,36.49.07,4.56,2.42,6.03,6.3,4.15,7.95-3.86,15.78-7.96,23.75-11.77,15.95-7.62,31.93-15.18,47.96-22.62,3.46-1.61,5.48-3.8,4.45-7.64-7.84-29.33-14.95-58.9-27.28-86.82-8.53-19.32-18.68-37.62-33.17-53.21-22.99-24.73-49.79-41.92-84.79-42.98-3.31-.1-6.61-.59-9.91-.92-11.09-1.1-16.69-2.27-17.4,12.51-.1,2.14-.96,4.3-1.75,6.35-13.15,34.36-38.43,56.53-78.41,59.09-36.5,2.33-62.9-13.44-79.36-45.83-3.73-7.35-6.4-15.25-9.37-22.97-2.52-6.54-3.93-8-10.92-6.94-13.65,2.08-27.34,4.17-40.75,7.36-11.85,2.82-22.62,8.55-33.09,14.94-20.06,12.25-33.43,30.19-44.65,50.2-19.05,33.97-31.18,70.65-42.32,107.72-1.51,5.02-.79,6.54,4.28,8.99,19.9,9.6,39.86,19.08,59.75,28.71,12.07,5.84,12.08,5.94,15.14-8.06,1.18-5.39,2.03-10.84,3.11-16.25,1.95-9.75,3.82-19.51,5.99-29.21.93-4.15,4.75-6.02,8.93-5.13,3.14.67,4.47,2.85,5.22,5.68,2.42,9.22,2.1,18.67,2.1,28.04.07,107.8,1.5,215.61-1.44,323.39-.02.55-.11,1.11-.05,1.66.32,3.07,1.77,5.22,5.06,5.36,3.31.14,6.62.02,9.94-.03,92.32-1.54,184.65-2.01,276.99-1.65,16.44.06,15.77,1.19,15.76-16.62-.01-53.08,0-106.16,0-159.23ZM548.4,277.54v.79c-3.3,0-6.6-.03-9.91,0-17.06.18-34.12.31-51.18.63-5.9.11-7.68,2.71-5.93,8.53,2.22,7.39,5.1,14.54,9.27,21.08,14.71,23.09,44.78,33.46,65.59,28.62,28.93-6.72,47.59-23.84,57.19-51.51,2.13-6.14.75-8.13-5.6-8.14-19.81-.02-39.62,0-59.43,0Z\"/> </svg>",
    bottoms: "<svg id=\"Layer_1\" data-name=\"Layer 1\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 1090 1080\"> <defs> <style> .cls-1 { fill: none; stroke: currentColor; stroke-linecap: round; stroke-width: 18px; } </style> </defs> <path class=\"cls-1\" d=\"M545.95,298.38c4.2,0,7.61,3.41,7.61,7.61s-3.41,7.61-7.61,7.61-7.61-3.41-7.61-7.61,3.41-7.61,7.61-7.61h0Z\"/> <line class=\"cls-1\" x1=\"546.19\" y1=\"305.49\" x2=\"545.88\" y2=\"305.49\"/> <path class=\"cls-1\" d=\"M507.95,801.54c-7.65,35.01-25.13,32.87-25.13,32.87,0,0-210.78-25.71-257.13-32.87-44.25-6.83-37.08-38.91-37.08-38.91l87.44-442.57\"/> <line class=\"cls-1\" x1=\"194.07\" y1=\"736.47\" x2=\"514.01\" y2=\"776.73\"/> <line class=\"cls-1\" x1=\"507.95\" y1=\"801.54\" x2=\"545\" y2=\"649.96\"/> <path class=\"cls-1\" d=\"M582.05,801.54c7.65,35.01,25.13,32.87,25.13,32.87,0,0,210.78-25.71,257.13-32.87,44.24-6.83,37.08-38.91,37.08-38.91l-87.44-442.57\"/> <line class=\"cls-1\" x1=\"895.93\" y1=\"736.47\" x2=\"575.99\" y2=\"776.73\"/> <line class=\"cls-1\" x1=\"582.05\" y1=\"801.54\" x2=\"545\" y2=\"649.96\"/> <path class=\"cls-1\" d=\"M276.05,320.06s72.25,27.2,265.76,27.2\"/> <path class=\"cls-1\" d=\"M807.57,320.06s-72.26,27.2-265.76,27.2\"/> <path class=\"cls-1\" d=\"M276.05,320.06v-53.08s-1.07-21.41,22.03-21.41,64.73,14.75,246.92,14.75\"/> <path class=\"cls-1\" d=\"M813.95,320.06v-53.08s1.07-21.41-22.03-21.41-64.73,14.75-246.92,14.75\"/> <path class=\"cls-1\" d=\"M249.33,470.63s140.56,2.84,142.08-127.69\"/> <path class=\"cls-1\" d=\"M842.43,470.63s-140.56,2.84-142.08-127.69\"/> <path class=\"cls-1\" d=\"M541.81,347.25v209.93s62.66-6.53,62.66-60.9v-149.04\"/> </svg>",
    accessories: "<svg id=\"Layer_1\" data-name=\"Layer 1\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 1090 1080\"> <defs> <style> .cls-1 { fill: none; stroke: currentColor; stroke-linecap: round; stroke-width: 18px; } </style> </defs> <g id=\"handbag\"> <path class=\"cls-1\" d=\"M806.99,574.93l30.02,240.19c2.61,20.85-13.65,39.26-34.66,39.26H287.65c-21.01,0-37.27-18.42-34.66-39.26l30.02-240.19\"/> <path class=\"cls-1\" d=\"M595.23,574.93h211.76v-104.8c0-38.58-31.28-69.86-69.86-69.86h-384.25c-38.58,0-69.86,31.28-69.86,69.86v104.8h215.41\"/> <path class=\"cls-1\" d=\"M545,225.61h0c67.48,0,122.26,54.78,122.26,122.26v52.4h-244.52v-52.4c0-67.48,54.78-122.26,122.26-122.26Z\"/> <rect class=\"cls-1\" x=\"498.42\" y=\"516.71\" width=\"93.15\" height=\"116.44\" rx=\"11.64\" ry=\"11.64\"/> </g> </svg>",
    shoes: "<svg id=\"Layer_1\" data-name=\"Layer 1\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 1090 1080\"> <defs> <style> .cls-1 { fill: none; stroke: currentColor; stroke-linecap: round; stroke-linejoin: round; stroke-width: 17px; } </style> </defs> <path class=\"cls-1\" d=\"M542.11,203.83c-.51,77.48-1.15,116.86-1.91,118.14s-2.17,2.29-4.21,3.06-7.52,1.15-16.44,1.15c8.92,0,4.46,0-13.38,0\"/> <path class=\"cls-1\" d=\"M376.95,633.56c-23.96-214.1-35.68-324.08-35.17-329.94s1.91-12.23,4.21-19.12,5.1-13.13,8.41-18.73c3.31-5.61,7.52-11.21,12.62-16.82s9.69-10.07,13.76-13.38,8.41-6.37,13-9.18c4.59-2.8,10.32-5.73,17.2-8.79,6.88-3.06,14.15-5.73,21.79-8.03,7.65-2.29,15.68-3.95,24.09-4.97-8.41,1.02-4.21.51,12.62-1.53\"/> <path class=\"cls-1\" d=\"M867.08,835.42c-33.13,9.69-60.41,16.95-81.82,21.79-21.41,4.84-41.67,8.67-60.79,11.47s-34.28,4.33-45.5,4.59c-11.21.25-21.66-.51-31.35-2.29s-17.59-3.82-23.7-6.12-22.68-11.21-49.7-26.76c-27.02-15.55-45.11-25.49-54.29-29.82-9.18-4.33-17.33-7.77-24.47-10.32-7.14-2.55-14.53-4.84-22.17-6.88-7.65-2.04-15.29-3.7-22.94-4.97s-20.14-2.8-37.47-4.59-29.95-2.8-37.85-3.06c7.9.25,3.95.13-11.85-.38\"/> <path class=\"cls-1\" d=\"M505.41,326.94v190.4\"/> <path class=\"cls-1\" d=\"M505.41,589.21c3.06-1.02,6.37-2.93,9.94-5.73s8.54-7.26,14.91-13.38,10.96-11.6,13.76-16.44,4.46-8.54,4.97-11.09.64-5.61.38-9.18-.76-6.12-1.53-7.65-2.8-3.82-6.12-6.88-7.39-4.84-12.23-5.35-9.94.13-15.29,1.91c5.35-1.78,2.68-.89-8.03,2.68\"/> <path class=\"cls-1\" d=\"M496.23,828.54c-.51,32.11-1.15,48.68-1.91,49.7s-18.48,1.4-53.14,1.15-56.58-14.66-65.76-43.2c-9.18-28.55-13.64-45.37-13.38-50.47-.25,5.1-.13,2.55.38-7.65\"/> <path class=\"cls-1\" d=\"M376.95,634.32c-2.55,4.59-5.61,12.36-9.18,23.32s-6.5,20.77-8.79,29.44-3.82,15.55-4.59,20.65-1.02,12.87-.76,23.32,1.78,20.77,4.59,30.97c-2.8-10.2-1.4-5.1,4.21,15.29\"/> <path class=\"cls-1\" d=\"M631.57,629.74c-8.16,3.06-13.89,6.24-17.2,9.56-3.31,3.31-5.61,6.63-6.88,9.94s-2.17,7.77-2.68,13.38-1.15,9.69-1.91,12.23-2.29,5.86-4.59,9.94-5.73,8.03-10.32,11.85c-4.59,3.82-8.54,6.5-11.85,8.03-3.31,1.53-7.9,2.93-13.76,4.21-5.86,1.27-13.13,1.91-21.79,1.91s-16.57-.64-23.7-1.91c-7.14-1.27-10.96-2.17-11.47-2.68.51.51.25.25-.76-.76\"/> <path class=\"cls-1\" d=\"M631.57,629.74c-2.04-17.33-2.93-28.42-2.68-33.26s5.61-52.25,16.06-142.22c10.45-89.97,15.68-137.63,15.68-142.99s-.38-10.2-1.15-14.53-2.8-10.7-6.12-19.12-7.01-15.42-11.09-21.03c-4.08-5.61-8.67-10.83-13.76-15.68s-9.43-8.54-13-11.09-9.3-5.86-17.2-9.94-16.82-7.65-26.76-10.7c-9.94-3.06-17.2-4.84-21.79-5.35,4.59.51,2.29.25-6.88-.76\"/> <path class=\"cls-1\" d=\"M631.57,629.74c2.04,2.04,4.97,8.03,8.79,17.97s8.28,19.37,13.38,28.29,10.45,17.08,16.06,24.47c5.61,7.39,11.85,14.53,18.73,21.41s13.64,12.87,20.26,17.97,15.04,10.45,25.23,16.06,19.12,9.81,26.76,12.62,14.53,4.97,20.65,6.5,15.29,3.31,27.53,5.35,21.41,4.33,27.53,6.88c6.12,2.55,11.21,5.73,15.29,9.56s6.88,7.01,8.41,9.56,2.93,5.61,4.21,9.18,2.17,7.65,2.68,12.23c-.51-4.59-.25-2.29.76,6.88\"/> <path class=\"cls-1\" d=\"M505.41,589.97l-.76,114.7\"/> <path class=\"cls-1\" d=\"M504.64,518.1c-10.7-3.57-17.84-5.35-21.41-5.35s-6.37.38-8.41,1.15-4.33,2.42-6.88,4.97-4.33,5.1-5.35,7.65-1.53,5.86-1.53,9.94,1.02,8.41,3.06,13,5.35,9.43,9.94,14.53c4.59,5.1,10.83,10.58,18.73,16.44-7.9-5.86-3.95-2.93,11.85,8.79\"/> <path class=\"cls-1\" d=\"M504.64,326.17c-19.37-.51-29.95-1.91-31.73-4.21s-2.8-22.68-3.06-61.17c.25,38.49.13,19.24-.38-57.73\"/> <path class=\"cls-1\" d=\"M497,828.54c8.16,2.55,29.69,13.64,64.61,33.26,34.92,19.63,55.95,30.59,63.08,32.88,7.14,2.29,14.91,4.08,23.32,5.35,8.41,1.27,19.5,1.66,33.26,1.15,13.76-.51,28.93-2.04,45.5-4.59s33.77-5.86,51.61-9.94c17.84-4.08,38.1-9.56,60.79-16.44s34.54-11.21,35.56-13,1.4-5.35,1.15-10.7-1.02-8.54-2.29-9.56c-1.27-1.02-2.42-1.53-3.44-1.53,1.02,0,.51,0-1.53,0\"/> <path class=\"cls-1\" d=\"M503.88,704.67c-6.63-.51-13-1.78-19.12-3.82-6.12-2.04-15.17-5.86-27.14-11.47-11.98-5.61-23.19-11.98-33.64-19.12s-20.01-14.66-28.67-22.56-13.76-12.11-15.29-12.62c1.53.51.76.25-2.29-.76\"/> <path class=\"cls-1\" d=\"M469.47,203.07c1.02-12.23,2.29-19.12,3.82-20.65s3.57-2.55,6.12-3.06c2.55-.51,11.47-.76,26.76-.76s24.21.25,26.76.76,4.59,1.53,6.12,3.06,2.42,5.23,2.68,11.09c-.25-5.86-.13-2.93.38,8.79\"/> </svg>",
    sweaters: "<svg id=\"Layer_1\" data-name=\"Layer 1\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 1090 1080\"> <defs> <style> .cls-1 { fill: none; stroke: currentColor; stroke-linecap: round; stroke-width: 18px; } .cls-2 { fill: currentColor; } </style> </defs> <g> <path class=\"cls-1\" d=\"M221.65,800.47l-10.42,55.06s40.95,20.45,87.31,12.9\"/> <line class=\"cls-1\" x1=\"298.54\" y1=\"868.43\" x2=\"309.95\" y2=\"813.36\"/> <line class=\"cls-1\" x1=\"309.95\" y1=\"813.36\" x2=\"221.65\" y2=\"800.47\"/> <path class=\"cls-1\" d=\"M309.95,813.36s60.48-136.05,75.9-215.79\"/> <path class=\"cls-1\" d=\"M221.65,800.47s-4.82-80.78,11.41-150.81\"/> <line class=\"cls-1\" x1=\"233.06\" y1=\"649.66\" x2=\"304.25\" y2=\"360.95\"/> <path class=\"cls-1\" d=\"M304.25,360.95s16.41-72.87,81.6-97.23c65.19-24.36,91.28-32.24,91.28-32.24\"/> <line class=\"cls-1\" x1=\"385.85\" y1=\"448.26\" x2=\"385.85\" y2=\"788.07\"/> <path class=\"cls-1\" d=\"M477.13,231.47s8.51,92.27,98.72,92.27\"/> <path class=\"cls-1\" d=\"M385.85,788.07s-10.1,36.71,190,36.71\"/> <path class=\"cls-1\" d=\"M397.26,802.3v60.17s-2.97,32.24,178.59,32.24\"/> <path class=\"cls-1\" d=\"M930.04,800.47l10.42,55.06s-40.95,20.45-87.31,12.9\"/> <line class=\"cls-1\" x1=\"853.15\" y1=\"868.43\" x2=\"841.74\" y2=\"813.36\"/> <line class=\"cls-1\" x1=\"841.74\" y1=\"813.36\" x2=\"930.04\" y2=\"800.47\"/> <path class=\"cls-1\" d=\"M841.74,813.36s-60.48-136.05-75.9-215.79\"/> <path class=\"cls-1\" d=\"M930.04,800.47s4.82-80.78-11.41-150.81\"/> <line class=\"cls-1\" x1=\"918.63\" y1=\"649.66\" x2=\"847.44\" y2=\"360.95\"/> <path class=\"cls-1\" d=\"M847.44,360.95s-16.41-72.87-81.6-97.23c-65.19-24.36-91.28-32.24-91.28-32.24\"/> <line class=\"cls-1\" x1=\"765.84\" y1=\"448.26\" x2=\"765.84\" y2=\"788.07\"/> <path class=\"cls-1\" d=\"M674.56,231.47s-8.5,92.27-98.72,92.27\"/> <path class=\"cls-1\" d=\"M765.84,788.07s10.1,36.71-189.99,36.71\"/> <path class=\"cls-1\" d=\"M754.43,802.3v60.17s2.97,32.24-178.58,32.24\"/> <path class=\"cls-1\" d=\"M477.13,231.47s44,22.82,98.72,22.82\"/> <path class=\"cls-1\" d=\"M674.56,231.47s-44,22.82-98.72,22.82\"/> </g> <path class=\"cls-2\" d=\"M575.81,634.83c-1.62,0-3.23-.45-4.66-1.26-1.55-.87-38.83-21.57-62.59-52.6-1.26-1.58-2.36-3.17-3.43-4.69-2.36-3.36-4.53-6.85-6.5-10.48-5.98-10.83-7.86-23.5-5.27-35.6,5.88-26.71,32.33-43.58,59.04-37.67,1.49.32,2.94.71,4.36,1.16,7.27,2.49,13.81,6.73,19.04,12.32,5.27-5.59,11.8-9.8,19.08-12.22,26.06-8.31,53.9,6.05,62.24,32.11.45,1.45.87,2.91,1.2,4.4,2.59,12.12.74,24.77-5.27,35.6-2.59,4.72-5.53,9.25-8.76,13.58-1.17,1.55-2.36,3.1-3.59,4.59-23.86,29.33-58.84,48.73-60.26,49.53-1.42.81-3.01,1.23-4.62,1.23h0ZM541.57,510.65c-7.76.07-15.2,3.1-20.79,8.47-4.36,4.14-7.4,9.5-8.67,15.39-1.58,7.57-.39,15.45,3.39,22.21,1.62,2.98,3.43,5.85,5.37,8.63.87,1.26,1.88,2.62,2.91,3.94,16.65,21.69,41.74,38.34,52.02,44.65,9.86-6.05,33.33-21.63,49.95-42.03,1.03-1.26,2.07-2.62,3.07-3.98,2.68-3.59,5.11-7.34,7.24-11.28,3.78-6.76,4.98-14.65,3.39-22.21-3.59-16.29-19.69-26.64-35.99-23.05-.81.19-1.62.39-2.42.65-6.37,2.23-11.74,6.6-15.23,12.38-3.52,5.53-10.86,7.18-16.39,3.65-1.45-.94-2.72-2.17-3.65-3.65-3.49-5.75-8.89-10.15-15.26-12.35-2.88-.94-5.89-1.42-8.96-1.42h0ZM541.57,510.65\"/> </svg>",
    outerwear: "<svg id=\"Layer_1\" data-name=\"Layer 1\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 1090 1080\"> <defs> <style> .cls-1 { fill: currentColor; } </style> </defs> <path class=\"cls-1\" d=\"M340.71,747.17c-1.77.86-2.87,1.06-2.99,1.51-3.18,12.38-6.46,24.73-9.32,37.19-4.49,19.49-8.36,39.13-13.1,58.56-6.09,24.97-14.72,31.82-40.86,31.21-17.85-.42-36-.83-53.42-4.27-28.57-5.64-48.77-13.32-46.51-57.21,2.28-44.3,4.35-88.62,7.76-132.84,4.02-52.03,9.09-104.03,18.09-155.46,5.67-32.38,13.79-64.32,25.97-95.01,17.06-42.98,45.04-77.74,80.88-106.44,15.87-12.71,34.53-20,53.48-26.72,8.48-3.01,20.35-1.77,24.31-10.85,3.18-7.3,1.31-16.83,1.54-25.37.16-5.72-.06-11.45.12-17.17.94-29.97,20.21-51.54,49.71-54.83,16.34-1.83,32.79-3.31,49.21-3.63,46.51-.91,93.03-1.17,139.53.8,8.56.36,17.08,1.56,25.62,2.39,20.16,1.96,34.12,13.46,45.05,29.47,4.56,6.68,6.58,14.11,6.52,22.27-.1,12.88.03,25.77.37,38.64.28,10.58.99,10.95,12.15,13.72,9.7,2.41,19.43,4.82,28.93,7.92,24.21,7.88,44.95,21.11,63.15,39.19,37.66,37.4,60.39,82.81,74.79,133.14,11.25,39.34,16.86,79.63,21.15,120.19,6.4,60.5,7.55,121.35,11.97,181.99,1.35,18.52.52,37.19.82,55.8.25,15.3-7.23,25.94-20.23,32.74-6.26,3.27-13.11,6.51-19.99,7.34-22.71,2.76-45.52,5.24-68.49,3.23-2.85-.25-5.67-.81-8.52-1.09-9.47-.95-15.65-5.98-19.39-14.68-5.39-12.54-9.22-25.55-12.31-38.78-3.26-13.92-6.1-27.94-9.07-41.93-1.01-4.77-2.6-9.21-5.01-13.07-4.98,1.57-4.19,4.84-4.23,7.27-.29,16.46.11,32.94-.62,49.37-.57,12.83-1.62,25.76-3.95,38.36-4.25,22.89-16.17,32.71-39.85,34.42-4.99.36-10.01.33-15.02.32-97.33-.28-194.66-.59-291.99-.9-2.86,0-5.73.03-8.59-.09-21.05-.87-31.28-8.62-36.41-29.43-2.37-9.63-2.89-19.77-3.78-29.72-2.61-29.02-4.99-58.06-7.49-87.53ZM755.79,555.84c-.13,0-.26,0-.39,0,0,23.63.43,47.27-.13,70.89-.49,20.8.6,41.07,5.11,61.69,10.24,46.81,20.49,93.61,32.16,140.08,1.39,5.53,3.4,10.93,5.51,16.24,1.66,4.18,4.6,7.27,9.57,7.31,21.44.18,43.01,1.88,64.12-3.24,20.69-5.02,23.37-8.64,23.76-29.22.08-4.29.16-8.6-.11-12.89-1.53-25.01-3.24-50.01-4.73-75.02-4.13-69.3-8.13-138.61-20.84-207.05-6.43-34.61-15.87-68.28-30.75-100.27-11.9-25.59-27.99-48.19-48.75-67.46-22.56-20.94-49.08-34.34-78.65-42.13-4.88-1.29-8.23.24-9.91,5.3-.89,2.69-2.17,5.32-3.66,7.74-18.46,29.96-42.94,49.45-80.48,47.27-15-.87-30.07-.59-45.11-.6-16.98,0-17.22.11-17.37,16.21-.26,28.64-.22,57.29-.26,85.93,0,2.85.23,5.72.55,8.55.36,3.19,2.26,5.25,5.45,5.56,4.26.41,8.56.66,12.84.66,46.55.03,93.1.08,139.64-.04,21.02-.05,20.99-.21,21.32-21.48.13-8.58.17-17.18.78-25.74.32-4.5,2.67-8.28,7.85-8.62,4.9-.32,8.01,2.73,9.95,6.78,2.88,6,2.53,12.53,2.52,18.98-.03,31.51-.01,63.02-.01,94.53ZM336.93,551.35h.43c0-31.48-.04-62.97.04-94.45.01-5.68-.77-11.54,1.82-16.9,1.65-3.42,4.55-5.52,8.44-5.63,4.4-.12,8.51.55,11.05,4.91,2.28,3.91,2.38,8.17,2.44,12.46.15,10.73.13,21.46.39,32.19.1,3.97,2.22,6.72,6.45,7.02,4.27.3,8.57.45,12.85.45,28.62.04,57.24.04,85.87.02,17.17,0,34.35.12,51.52-.13,14.02-.2,15.1-1.24,15.21-14.21.27-30.05.34-60.1.46-90.16,0-2.13-.18-4.28-.46-6.39-.44-3.25-2.6-5.1-5.66-5.42-4.25-.44-8.55-.59-12.82-.59-19.32-.02-38.69.96-57.95-.1-29.17-1.61-51.88-14.6-65.24-41.58-2.22-4.48-4.03-9.15-6.23-13.64-1.8-3.67-4.68-4.97-8.77-3.56-28.74,9.89-54.82,23.87-76.79,45.53-30.32,29.89-50.44,65.29-62.34,105.99-13.52,46.22-18.73,93.85-25.13,141.27-3.44,25.49-5.75,51.17-7.78,76.82-3.49,44.2-6.36,88.44-9.47,132.67-.15,2.14-.2,4.3-.07,6.44.74,13.02,6.01,19.61,18.8,22.36,14.69,3.15,29.54,5.38,44.65,5.44,33.16.12,30.15,4.48,38.27-28.73,11.03-45.12,21.18-90.47,32.76-135.44,4.32-16.8,7.28-33.47,7.27-50.74-.02-28.62,0-57.24,0-85.87ZM545.33,208.29s0-.07,0-.1c-27.92,0-55.85-.23-83.77.11-11.41.14-22.78,1.08-33.64,5.7-12.39,5.28-19.84,13.56-20.08,27.55-.33,18.61-1.18,37.22-1.13,55.82.02,6.33,1.43,12.87,3.35,18.95,6.67,21.13,22.4,32.83,44.49,33.4,11.45.29,22.91.09,34.37.08,11.46,0,22.92.18,34.37-.15,7.84-.23,9.92-2.46,10.14-10.54.35-12.88.43-25.78.37-38.67-.03-6.88-2.43-9.88-8.28-10.05-10.73-.31-21.48-.06-32.22-.24-7.15-.12-14.33-.22-21.43-1.01-7.28-.81-9.97-4.25-10.17-11.32-.21-7.26,2.6-10.67,9.74-11.25,4.27-.35,8.59-.27,12.88-.28,40.1-.07,80.21-.12,120.31-.14,4.29,0,8.59.11,12.87.34,5.31.28,7.33,3.61,7.36,8.51.02,2.86.13,5.74-.14,8.58-.38,3.92-2.4,6.74-6.58,7.05-4.27.32-8.57.35-12.86.32-13.61-.09-27.21-.41-40.81-.37-6.78.02-8.91,2.06-9.07,9.03-.32,13.6-.4,27.21-.22,40.81.1,6.87,2.27,9.22,8.95,9.25,25.78.08,51.55-.03,77.33-.46,4.11-.07,8.77-1.2,12.17-3.39,15.76-10.14,27.12-23.72,27.65-43.35.48-17.88.41-35.83-.74-53.67-1.31-20.38-14.31-34.41-34.4-38.45-6.97-1.4-14.21-1.92-21.33-1.98-26.49-.22-52.99-.09-79.48-.09ZM642.3,645.79v.26c26.49,0,52.98.14,79.47-.07,10.87-.09,12.2-1.5,12.35-12.65.23-16.46-.03-32.93,0-49.4.05-18.61.41-37.23.21-55.84-.11-10.9-1.84-12.52-12.41-12.69-15.75-.24-31.5-.11-47.25-.13-35.8-.04-71.59-.16-107.39-.05-11.06.03-12.13,1.05-12.21,12.58-.26,35.08-.35,70.16-.24,105.24.03,11.21,1.45,12.6,12.29,12.69,25.06.2,50.11.06,75.17.06ZM448.44,666.97v-.76c-25.02,0-50.03-.13-75.05.06-10.22.08-11.62,1.48-12.01,10.63-1.65,37.99,3.97,75.6,6.05,113.39.28,5.07,3.76,7.41,8.55,7.8,2.84.23,5.71.19,8.57.22,41.45.41,82.91.82,124.36,1.2,5,.05,10.02.12,15-.19,6.66-.41,9.01-2.59,9.23-9.22.31-9.28.12-18.58.11-27.87-.02-27.88.1-55.75-.19-83.63-.09-9.03-2.59-11.45-11.73-11.55-24.3-.26-48.6-.09-72.9-.09ZM643.89,670.03c0-.49,0-.99,0-1.48-17.88,0-35.76.04-53.63-.02-9.29-.03-18.59-.29-27.88-.3-4.17,0-6.54,2.54-7.13,6.46-.42,2.81-.33,5.71-.33,8.56-.04,32.9-.06,65.81-.04,98.71,0,3.57.07,7.16.45,10.7.41,3.9,2.52,6.64,6.65,7.05,3.54.36,7.13.48,10.69.49,43.63.17,87.27.31,130.9.42,4.29.01,8.59.02,12.86-.34,6.41-.54,8.9-2.77,9.71-9.21.8-6.38,1.31-12.82,1.44-19.25.45-22.89,1.3-45.74,3.72-68.53.9-8.5.63-17.14.63-25.72,0-4.06-2.46-6.64-6.52-6.98-4.98-.42-9.99-.54-14.98-.55-22.17-.05-44.35-.02-66.52-.02ZM444.19,644.39c0,.2,0,.4,0,.6,18.59,0,37.17-.03,55.76.01,7.86.02,15.73.38,23.59.21,6.84-.15,9.22-2.47,9.75-8.67.18-2.13.14-4.29.14-6.43,0-33.6-.01-67.2-.02-100.8,0-.71.01-1.43-.02-2.14-.45-9.63-2.15-11.56-10.98-11.96-3.57-.16-7.15.02-10.72.03-36.46.04-72.92.07-109.38.1-10.01,0-20.02-.2-30.02,0-9.45.19-10.95,1.5-11.02,11.54-.24,35.03-.27,70.06-.13,105.09.04,11.03,1.66,12.38,12.3,12.41,23.59.06,47.18.02,70.77.02ZM455.35,872.25c18.53,0,37.06.02,55.6-.02,4.99-.01,9.99-.01,14.95-.41,3.84-.31,7.1-2.44,7.17-6.5.19-12.09.09-24.19-.32-36.27-.13-3.8-2.83-6.47-6.79-7.04-2.8-.4-5.68-.42-8.52-.44-43.48-.21-86.96-.41-130.44-.57-3.55-.01-7.14.1-10.65.6-3.11.44-5.14,2.71-4.92,5.83.65,9.23,1.28,18.49,2.61,27.63,2.06,14.21,4.99,16.89,19.31,17.1,20.67.3,41.34.08,62.01.08ZM639.3,824.56c0-.56,0-1.13,0-1.69-15.71,0-31.44-.46-47.12.15-11.86.46-24.07-3.75-35.6,2.17-3.07,12.68-1.37,25.56-1.45,38.36-.03,4.79,2.62,8,7.74,8.39,2.84.21,5.7.28,8.56.28,40.01.03,80.01.06,120.02,0,4.99,0,10.03-.16,14.94-.92,7.84-1.21,13.1-5.82,14.25-13.84,1.01-7.05,1.48-14.19,1.76-21.31.4-10.32-.59-11.49-10.24-11.55-24.29-.14-48.58-.05-72.87-.05Z\"/> </svg>",
    swim: "<svg id=\"Layer_1\" data-name=\"Layer 1\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 1090 1080\"> <defs> <style> .cls-1 { fill: currentColor; } </style> </defs> <path class=\"cls-1\" d=\"M781.06,566.06c.55,5.16.83,7.77.83,7.83,2.82,25.25,8.85,49.46,18.1,72.63,5.38,13.52,13.39,28.35,18.79,41.86,4.23,10.58-1.66,17.35-10.2,22.62-32.52,20-61.68,43.53-87.48,70.59-40.12,42.08-72.95,92.53-98.3,144.42-3.84,7.84-8.87,9.18-17.79,9.22-16.64.06-28.83.01-36.58-.13-29.24-.52-63.69-.44-86.75-.3-5.55.03-9.78-2.71-12.72-8.21-3.95-7.41-7.97-14.86-12.04-22.36-19.9-36.55-42.5-70.05-67.81-100.51-6.74-8.11-15.58-17.77-26.51-28.96-25.72-26.33-54.54-48.71-86.46-67.12-8.26-4.78-7.26-14.67-3.5-21.85,22.88-43.77,35.07-90.18,38.54-138.42,5.06-70.33-6.51-142.11-28.51-209.12-2.08-6.35-3.44-10.83-4.09-13.43-1.67-6.72,1.83-12.41,7.11-16.92,23.97-20.4,40.17-45.54,49.45-74.84,7.48-23.67,11.58-48.94,12.28-75.79.1-4.02,1.81-7.29,5.11-9.81,1.95-1.48,5.02-2.24,9.22-2.28,16.21-.17,30.2-.05,41.94.35,8.69.29,13.05,4.69,13.08,13.19.05,14.88.71,29.4,1.98,43.54,3.29,36.36,12.42,73.54,34.31,102.49,16.81,22.21,42.67,35.52,70.27,39.39,27.14,3.8,53.27.31,78.41-10.47,35.86-15.39,54.64-56.33,63.24-91.87,6.34-26.22,9.36-53.95,9.06-83.18-.07-7.93,4.14-12.31,12.65-13.14,6.17-.59,19.59-.83,40.26-.73,4.62.02,7.82.42,9.61,1.21,8.68,3.79,6.51,12.69,7.49,20.77,0,.02.07,1.74.21,5.16,2.11,52.48,22.03,106.66,65.3,139.3,1.54,1.15,2.77,3.44,3.71,6.87,1.02,3.76.59,8.47-1.31,14.14-7.34,21.99-13.59,43.22-18.74,63.68-14.2,56.36-18.25,113.08-12.15,170.16ZM334.18,473.85c1.05,14.72,1.6,26.43,1.66,35.13,0,.67.14,2.88.41,6.65.3,4.3-.39,9.02-.48,12.72-.05,2.36-.1,4.39-.15,6.1-1.68,54.68-14.34,106.95-37.98,156.81-.36.76-.09,1.66.62,2.08,17.65,10.65,34.79,22.96,51.43,36.94,57.5,48.28,103.39,110.99,136.77,178.58,1.05,2.15,2.36,3.05,4.71,3.03,16.31-.07,53.02-.09,110.13-.07,1.07,0,2.04-.6,2.5-1.56,31.14-64.16,73.52-123.62,126.4-170.85,19.66-17.55,40.6-33.06,62.82-46.51.81-.49,1.1-1.51.67-2.35-22.46-43.91-35.2-90.55-38.2-139.95-.34-5.56-.66-16.21-.96-31.94-.07-3.54.72-7.55.48-11.23-.18-2.79-.14-6.81.11-12.04,2.71-57.55,13.46-112.31,32.25-164.27,1.04-2.88,1.28-5.17-.98-7.22-25.66-23.4-43.71-51.23-54.15-83.51-7.35-22.73-11.37-46.18-12.04-70.34-.03-1.07-.92-1.91-1.99-1.89-.02,0-.04,0-.06,0l-16.69.97c-1.59.09-2.86,1.33-2.98,2.91-.37,4.56-1.33,15.47-2.89,32.71-2.93,32.35-11.72,63.31-26.37,92.88-16.29,32.82-43.76,56.99-78.98,66.57-18.28,4.97-40,5.94-58.87,4.67-14-.96-27.38-3.7-40.13-8.23-57.54-20.43-84.77-77.07-94-133.89-2.13-13.14-3.41-25.27-3.84-36.38-.32-8.26-.51-15.04-.58-20.35-.01-1.02-.85-1.83-1.87-1.82-.03,0-.06,0-.09,0l-19.73,1.14c-.73.05-1.11.44-1.14,1.17-2.43,49.31-16.51,97.78-48.05,136.04-5.42,6.58-11.28,12.97-17.56,19.18-.33.32-.45.81-.3,1.25,12.48,35.82,21.22,72.66,26.22,110.51,1.88,14.29,3.18,26.4,3.89,36.35Z\"/> <path class=\"cls-1\" d=\"M599.81,631.66c-1.17,0-2.34-.28-3.41-.84l-51.4-27.02-51.4,27.02c-2.47,1.3-5.46,1.08-7.71-.56-2.26-1.64-3.38-4.42-2.91-7.16l9.82-57.23-41.58-40.53c-2-1.95-2.71-4.86-1.85-7.51s3.15-4.58,5.91-4.98l57.47-8.35,25.7-52.07c1.23-2.5,3.78-4.08,6.57-4.08s5.33,1.58,6.57,4.08l25.7,52.07,57.47,8.35c2.76.4,5.05,2.33,5.91,4.98.86,2.65.14,5.56-1.85,7.51l-41.58,40.53,9.82,57.23c.47,2.75-.66,5.52-2.91,7.16-1.28.93-2.79,1.4-4.31,1.4ZM545,588.2c1.17,0,2.34.28,3.41.84l41.67,21.91-7.96-46.4c-.41-2.38.38-4.8,2.11-6.48l33.71-32.86-46.59-6.77c-2.39-.35-4.45-1.84-5.51-4.01l-20.84-42.22-20.84,42.22c-1.07,2.16-3.13,3.66-5.51,4.01l-46.59,6.77,33.71,32.86c1.73,1.68,2.51,4.11,2.11,6.48l-7.96,46.4,41.67-21.91c1.07-.56,2.24-.84,3.41-.84Z\"/> </svg>",
    rompers: "<svg id=\"Layer_1\" data-name=\"Layer 1\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 1090 1080\"> <defs> <style> .cls-1 { fill: none; stroke: currentColor; stroke-linecap: round; stroke-width: 18px; } </style> </defs> <path class=\"cls-1\" d=\"M338.78,119.48c26.49-7.57,56.76-15.14,73.78-9.46,9.46,3.78,11.35,18.92,15.14,39.73,15.14,73.78,54.86,136.22,117.3,181.62,62.43-45.41,102.16-107.84,117.3-181.62,3.78-20.81,5.68-35.95,15.14-39.73,17.03-5.68,47.3,1.89,73.78,9.46l-5.68,130.54c0,30.27,7.57,62.43,24.59,96.49l-32.16,206.22c45.41,94.59,87.03,211.89,107.84,344.32l-259.19,73.78c-15.14,3.78-26.49-1.89-34.05-17.03l-7.57-15.14-7.57,15.14c-7.57,15.14-18.92,20.81-34.05,17.03l-259.19-73.78c20.81-132.43,62.43-249.73,107.84-344.32l-32.16-206.22c17.03-34.05,24.59-66.22,24.59-96.49l-5.68-130.54Z\"/> <path class=\"cls-1\" d=\"M352.03,550.83h385.95\"/> <path class=\"cls-1\" d=\"M465.54,562.18c0,30.27-3.78,56.76-9.46,79.46\"/> <path class=\"cls-1\" d=\"M545,562.18v88.92\"/> <path class=\"cls-1\" d=\"M624.46,562.18c0,30.27,3.78,56.76,9.46,79.46\"/> </svg>",
    baby: "<svg id=\"Layer_1\" data-name=\"Layer 1\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 1090 1080\"> <defs> <style> .cls-1 { fill: currentColor; } </style> </defs> <path class=\"cls-1\" d=\"M876.46,336.71c-52.87-74.24-110.74-112.73-149.98-131.95-43.02-21.06-73.57-24.33-74.85-24.46-6.03-.59-11.5,3.38-13.01,9.45-30.78,124.09-86.34,127.79-92.57,127.8h-2.1c-6.23-.01-61.79-3.71-92.57-127.8-1.5-6.07-6.97-10.04-13.01-9.45-1.28.13-31.83,3.39-74.85,24.46-39.24,19.21-97.11,57.7-149.98,131.95-3.61,5.08-3.11,12,1.19,16.45l65.53,67.86h0c2.31,2.39,5.37,3.71,8.64,3.71h46.53v304.81c0,6.9,5.22,12.46,11.88,12.65.32,0,32.01,1.14,63.77,17.67,41.63,21.67,63.32,58.81,64.45,110.37.08,3.71,1.72,7.18,4.5,9.52.97.82,24.19,20,64.96,20s63.99-19.18,64.96-20c2.78-2.34,4.41-5.81,4.5-9.52,1.13-51.33,22.63-88.37,63.91-110.09,31.72-16.69,64-17.95,64.32-17.96,6.66-.19,11.88-5.74,11.88-12.65v-304.81h46.53c3.26,0,6.33-1.32,8.64-3.71l65.53-67.86c4.31-4.46,4.81-11.38,1.19-16.45ZM347.65,399.43h-53.69l-54.44-56.37c47.93-64.17,99.01-97.93,133.55-114.98,25.46-12.57,46.25-18.31,57.57-20.78,35.43,128.84,100.49,135.54,113.25,135.56h2.2c12.75-.03,77.82-6.72,113.24-135.56,28.18,6.18,114.25,32.79,191.13,135.76l-54.44,56.37h-53.69c-6.74,0-12.22,5.68-12.22,12.65v305.88c-13.52,1.76-37.7,6.53-62.19,19.28-33.33,17.35-73.74,53.22-77.65,125.69-6.68,3.95-22.44,11.5-45.29,11.5s-38.61-7.55-45.29-11.5c-3.91-72.47-44.32-108.34-77.65-125.7-24.49-12.75-48.67-17.52-62.19-19.28v-305.88c0-6.98-5.48-12.65-12.22-12.65Z\"/> <path class=\"cls-1\" d=\"M592.42,619.31c-1.01,0-2.02-.24-2.95-.73l-44.47-23.38-44.47,23.38c-2.14,1.12-4.72.94-6.67-.48-1.95-1.42-2.93-3.82-2.52-6.2l8.49-49.52-35.98-35.07c-1.73-1.68-2.35-4.2-1.6-6.5s2.73-3.97,5.12-4.31l49.72-7.23,22.24-45.06c1.07-2.16,3.27-3.53,5.68-3.53s4.62,1.37,5.68,3.53l22.24,45.06,49.72,7.23c2.39.35,4.37,2.02,5.12,4.31.75,2.29.12,4.81-1.6,6.5l-35.98,35.07,8.49,49.52c.41,2.38-.57,4.78-2.52,6.2-1.1.8-2.41,1.21-3.73,1.21ZM545,581.71c1.01,0,2.03.24,2.95.73l36.06,18.96-6.89-40.15c-.35-2.06.33-4.15,1.82-5.61l29.17-28.43-40.31-5.86c-2.06-.3-3.85-1.6-4.77-3.47l-18.03-36.53-18.03,36.53c-.92,1.87-2.71,3.17-4.77,3.47l-40.31,5.86,29.17,28.43c1.49,1.46,2.17,3.55,1.82,5.61l-6.89,40.15,36.06-18.96c.92-.49,1.94-.73,2.95-.73Z\"/> </svg>",
    baskets: "<svg id=\"Layer_1\" data-name=\"Layer 1\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 1090 1080\"> <defs> <style> .cls-1 { fill: none; stroke: currentColor; stroke-linecap: round; stroke-linejoin: round; stroke-width: 22px; } </style> </defs> <path class=\"cls-1\" d=\"M240,470 h610 l-70,410 c-6,36 -34,60 -70,60 h-330 c-36,0 -64,-24 -70,-60 z\"/> <path class=\"cls-1\" d=\"M370,470 v-30 c0,-96 78,-175 175,-175 s175,79 175,175 v30\"/> <line class=\"cls-1\" x1=\"305\" y1=\"595\" x2=\"785\" y2=\"595\"/> <line class=\"cls-1\" x1=\"330\" y1=\"720\" x2=\"760\" y2=\"720\"/> <line class=\"cls-1\" x1=\"400\" y1=\"470\" x2=\"400\" y2=\"875\"/> <line class=\"cls-1\" x1=\"545\" y1=\"470\" x2=\"545\" y2=\"895\"/> <line class=\"cls-1\" x1=\"690\" y1=\"470\" x2=\"690\" y2=\"875\"/> </svg>",
    women: "<svg id=\"Layer_1\" data-name=\"Layer 1\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 1090 1080\"> <defs> <style> .cls-1 { fill: currentColor; } </style> </defs> <path class=\"cls-1\" d=\"M544.16,932.2c-73.42,0-146.84,0-220.25,0-7.08,0-14.18.28-21.22-.2-16.38-1.12-23.86-9.56-22.12-26.25,1.38-13.15,3.91-26.21,6.57-39.18,5.68-27.69,11.91-55.27,17.66-82.95,3.61-17.38,4.91-35.04,4.78-52.76-.37-49.54-2.81-98.95-7.54-148.31-4.05-42.23-6.46-84.63-9.54-126.95-2.64-36.33,11.52-65.46,38.63-88.79,7.36-6.33,15.07-12.37,23.21-17.65,9.91-6.42,13.69-14.54,13.53-26.57-.61-45.99-.23-91.99-.17-137.99,0-7.07-.15-14.18.48-21.21.89-9.91,7.26-16.47,14.41-15.69,6.61.72,11.74,6.51,12.41,14.9.49,6.16.29,12.37.3,18.56.06,46.88.2,93.76.02,140.64-.03,8.96,2.32,14.43,11.77,17.71,34.8,12.09,63.51,34.52,92.06,56.87,13.91,10.89,26.8,23.07,40.25,34.55,6.7,5.72,8.83,5.49,15.91-.17,17.94-14.35,35.79-28.83,53.96-42.88,21.75-16.81,44.76-31.79,69.96-42.85,12.24-5.37,16.82-12.93,16.55-26.44-.96-46.87-.77-93.76-.88-140.64-.02-7.03.2-14.14,1.25-21.07.66-4.32,3.27-8.21,8.36-9.11,4.72-.84,8.63.82,12.01,4.01,4.03,3.8,6.42,8.38,6.52,14,.13,7.07.28,14.15.28,21.22.02,45.11.44,90.23-.27,135.33-.22,13.86,3.55,23.17,16,30.37,13.81,7.99,24.58,20.14,35.16,32.07,17.17,19.36,26.13,41.96,24.52,68-1.91,30.87-4.42,61.71-6.76,92.56-1.47,19.39-3.98,38.74-4.5,58.15-1.28,47.74-2.53,95.45-7.76,142.98-.65,5.94.76,12.27,2.01,18.25,8.65,41.52,17.67,82.97,26.17,124.52,1.58,7.71,2.01,15.82,1.74,23.71-.33,9.85-6.23,16.69-16.13,18.21-8.67,1.33-17.61,1.22-26.43,1.23-74.3.08-148.61.05-222.91.05v-.23ZM544.73,743.24v.19c62.76,0,125.53.11,188.29-.06,20.44-.06,20.74-.65,21.51-20.64.2-5.3.23-10.61.24-15.91.11-58.41,6.28-116.42,11.35-174.5,2.31-26.4,4.91-52.76,5.15-79.34.16-17.87-5.53-32.83-16.58-46.25-11.3-13.71-24.71-25.21-38.17-36.69-7.88-6.71-15.28-6.56-24.21-1.78-30.44,16.3-59.85,34.11-86.99,55.57-16.67,13.18-32.21,27.56-47.41,42.38-7.67,7.47-12.28,7.24-20.65.25-4.74-3.96-9.02-8.46-13.55-12.67-28.57-26.54-58.68-51.37-92.45-70.82-50.29-28.96-39.25-28.42-83.78,7.42-20.74,16.7-29.5,39-27.53,65.64,2.67,36.13,5.09,72.29,7.62,108.43,3.32,47.58,6.71,95.15,9.9,142.74.59,8.8.26,17.67.53,26.5.15,5.02,2.94,8.39,7.89,8.89,6.13.62,12.34.63,18.51.64,60.11.04,120.22.02,180.33.02ZM781.54,900.25c-7-35.9-13.78-70.47-20.48-105.07-4.73-24.42-5.13-24.81-31.27-24.9-114-.39-228.01-.75-342.01-1.13-14.14-.05-28.28-.37-42.42-.18-10.39.14-11.49,1.49-13.54,11.96-6.26,32.03-12.33,64.11-18.97,96.07-5.36,25.8-5.68,25.86,21.81,25.88,139.64.1,279.27.16,418.91.11,8.72,0,17.67,1.45,27.98-2.75Z\"/> </svg>",
    toys: "<svg id=\"Layer_1\" data-name=\"Layer 1\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 1090 1080\"> <defs> <style> .cls-1 { fill: currentColor; } </style> </defs> <path class=\"cls-1\" d=\"M793.87,720.05c-.39,0-.77-.03-1.16-.06-5.27-.65-9.05-5.46-8.41-10.73.74-6.11,1.13-12.25,1.13-18.4,0-72.23-53.22-139.32-135.63-170.94-4.98-1.91-7.47-7.5-5.56-12.45,1.91-4.98,7.5-7.47,12.45-5.56,43.42,16.65,80.21,42.71,106.43,75.27,27.19,33.82,41.58,73.13,41.58,113.68,0,6.95-.42,13.87-1.26,20.76-.58,4.82-4.69,8.44-9.57,8.44h0ZM793.87,720.05\"/> <path class=\"cls-1\" d=\"M545.02,900.01c-30.81.06-61.46-4.36-90.98-13.16-5.11-1.52-7.99-6.92-6.47-12.03,1.52-5.11,6.92-7.99,12.03-6.47,55.77,16.49,115.1,16.49,170.87,0,.9-.26,1.84-.42,2.78-.42,5.33-.03,9.67,4.24,9.7,9.57.03,4.4-2.94,8.28-7.21,9.41-29.45,8.76-60.01,13.19-90.72,13.09h0ZM545.02,900.01\"/> <g> <path class=\"cls-1\" d=\"M545.02,740.1c-1.62,0-3.23-.45-4.66-1.26-1.55-.87-38.83-21.57-62.59-52.6-1.26-1.58-2.36-3.17-3.43-4.69-2.36-3.36-4.53-6.85-6.5-10.48-5.98-10.83-7.86-23.5-5.27-35.6,5.88-26.71,32.33-43.58,59.04-37.67,1.49.32,2.94.71,4.36,1.16,7.27,2.49,13.81,6.73,19.04,12.32,5.27-5.59,11.8-9.8,19.08-12.22,26.06-8.31,53.9,6.05,62.24,32.11.45,1.45.87,2.91,1.2,4.4,2.59,12.12.74,24.77-5.27,35.6-2.59,4.72-5.53,9.25-8.76,13.58-1.17,1.55-2.36,3.1-3.59,4.59-23.86,29.33-58.84,48.73-60.26,49.53-1.42.81-3.01,1.23-4.62,1.23h0ZM510.78,615.91c-7.76.07-15.2,3.1-20.79,8.47-4.36,4.14-7.4,9.5-8.67,15.39-1.58,7.57-.39,15.45,3.39,22.21,1.62,2.98,3.43,5.85,5.37,8.63.87,1.26,1.88,2.62,2.91,3.94,16.65,21.69,41.74,38.34,52.02,44.65,9.86-6.05,33.33-21.63,49.95-42.03,1.03-1.26,2.07-2.62,3.07-3.98,2.68-3.59,5.11-7.34,7.24-11.28,3.78-6.76,4.98-14.65,3.39-22.21-3.59-16.29-19.69-26.64-35.99-23.05-.81.19-1.62.39-2.42.65-6.37,2.23-11.74,6.6-15.23,12.38-3.52,5.53-10.86,7.18-16.39,3.65-1.45-.94-2.72-2.17-3.65-3.65-3.49-5.75-8.89-10.15-15.26-12.35-2.88-.94-5.89-1.42-8.96-1.42h0ZM510.78,615.91\"/> <path class=\"cls-1\" d=\"M545.02,551.19c-53.12,0-103.11-18.78-140.74-52.86-37.89-34.34-58.78-80.05-58.78-128.75s20.89-94.44,58.78-128.78c37.63-34.08,87.65-52.86,140.74-52.86s103.11,18.78,140.71,52.86c37.89,34.34,58.78,80.05,58.78,128.78s-20.89,94.41-58.78,128.75c-37.6,34.11-87.62,52.86-140.71,52.86h0ZM545.02,207.24c-99.39,0-180.22,72.84-180.22,162.34s80.83,162.34,180.22,162.34,180.22-72.81,180.22-162.34-80.83-162.34-180.22-162.34h0ZM545.02,207.24\"/> <path class=\"cls-1\" d=\"M368.45,315.78c-.84,0-1.68-.13-2.49-.32-34.95-9.47-59.26-41.16-59.36-77.37,0-44.23,36.18-80.15,80.64-80.15,35.6-.06,66.99,23.25,77.27,57.32,1.97,4.95-.45,10.54-5.4,12.51-4.95,1.97-10.54-.45-12.51-5.4-.19-.52-.36-1.07-.48-1.62-9.38-32.53-43.36-51.28-75.85-41.9-32.53,9.38-51.28,43.36-41.9,75.85,5.92,20.5,22.05,36.44,42.65,42.09,5.14,1.36,8.21,6.66,6.82,11.8-1.16,4.23-5.01,7.18-9.38,7.18h0ZM368.45,315.78\"/> <path class=\"cls-1\" d=\"M721.58,315.78c-5.33-.03-9.63-4.4-9.57-9.73.03-4.3,2.91-8.08,7.08-9.25,32.4-9.21,51.18-42.94,41.97-75.33-9.21-32.4-42.94-51.18-75.33-41.97-20.05,5.69-35.79,21.24-41.74,41.22-1.39,5.14-6.69,8.18-11.83,6.79-5.14-1.39-8.18-6.69-6.79-11.84.03-.16.1-.32.13-.45,10.28-34.11,41.71-57.42,77.34-57.36,44.46,0,80.64,35.98,80.64,80.15-.1,36.21-24.38,67.93-59.33,77.43-.87.19-1.71.32-2.55.32h0ZM721.58,315.78\"/> <path class=\"cls-1\" d=\"M296.16,720.05c-4.88,0-8.99-3.65-9.57-8.47-.84-6.89-1.26-13.8-1.26-20.76,0-40.54,14.39-79.83,41.55-113.68,26.19-32.56,62.98-58.62,106.43-75.27,4.98-1.91,10.54.58,12.45,5.56,1.91,4.98-.58,10.54-5.56,12.45-82.41,31.62-135.67,98.71-135.67,170.94,0,6.14.39,12.29,1.13,18.39.65,5.27-3.1,10.09-8.41,10.74-.32.06-.71.1-1.1.1h0ZM296.16,720.05\"/> <path class=\"cls-1\" d=\"M379.29,922.13c-27.93,0-57.23-9.21-82.64-28.09-29.58-22.02-49.34-54.12-54.22-88.01-4.07-28.39,2.65-55.93,18.95-77.63,37.05-49.18,113.13-54.96,169.61-12.9,29.58,22.02,49.34,54.09,54.22,88.01,4.07,28.39-2.65,55.93-18.95,77.63-20.4,27.06-52.67,41-86.97,41h0ZM348.34,706.6c-28.39,0-54.87,11.28-71.52,33.4-13.19,17.52-18.62,39.96-15.26,63.24,4.14,28.81,21.14,56.29,46.65,75.27,21.95,16.26,47.24,24.34,71.16,24.34,28.39,0,54.87-11.28,71.52-33.4,13.19-17.53,18.59-39.96,15.26-63.24-4.14-28.81-21.15-56.35-46.65-75.27-21.95-16.3-47.3-24.35-71.16-24.35h0ZM348.34,706.6\"/> <path class=\"cls-1\" d=\"M710.75,922.13c-34.34,0-66.6-13.9-87-41.03-16.26-21.63-23.02-49.18-18.95-77.63,4.88-33.88,24.64-65.96,54.22-88.01,56.48-42.06,132.56-36.24,169.61,12.9,16.26,21.63,23.02,49.18,18.95,77.63-4.85,33.88-24.61,65.96-54.22,88.01-25.35,18.91-54.67,28.13-82.61,28.13h0ZM741.69,706.6c-23.93,0-49.21,8.02-71.16,24.35-25.51,18.98-42.58,46.43-46.65,75.27-3.33,23.28,2.07,45.72,15.26,63.24,30.68,40.7,94.7,44.78,142.74,9.05,25.51-19.01,42.48-46.43,46.66-75.27,3.33-23.28-2.07-45.75-15.26-63.24-16.71-22.05-43.19-33.4-71.58-33.4h0ZM741.69,706.6\"/> <path class=\"cls-1\" d=\"M717.83,872.56c-19.88,0-38.64-8.11-50.54-23.89-9.6-12.96-13.58-29.26-11.09-45.2,2.78-19.46,14.1-37.83,31.01-50.44,32.43-24.15,76.3-20.63,97.77,7.86,9.6,12.96,13.58,29.26,11.09,45.2-2.78,19.46-14.1,37.83-31.01,50.44-13.64,10.28-30.2,15.91-47.24,16.04h0ZM698.72,768.52c-12.84,9.57-21.37,23.28-23.44,37.7-1.78,10.87.9,21.95,7.4,30.84,15.1,20.05,46.88,21.82,70.84,4.01,12.83-9.57,21.37-23.31,23.44-37.73,1.78-10.87-.91-21.95-7.4-30.84-15-20.01-46.85-21.82-70.84-3.98h0ZM698.72,768.52\"/> <path class=\"cls-1\" d=\"M372.2,872.56c-17.04-.13-33.62-5.76-47.24-16.04-16.91-12.61-28.23-30.97-31.01-50.44-2.52-15.94,1.49-32.2,11.09-45.17,21.47-28.48,65.31-32.01,97.77-7.86,16.91,12.51,28.23,30.97,31.01,50.44,2.52,15.94-1.49,32.23-11.09,45.2-11.93,15.75-30.68,23.86-50.54,23.86h0ZM355.52,756.2c-13.97,0-26.93,5.5-35.05,16.26-6.5,8.86-9.15,19.98-7.4,30.84,2.07,14.42,10.61,28.16,23.44,37.73,23.96,17.85,55.74,16.04,70.84-4.01,6.5-8.89,9.15-19.98,7.4-30.84-2.07-14.42-10.6-28.16-23.44-37.7-10.31-7.82-22.89-12.12-35.82-12.29h.03ZM355.52,756.2\"/> </g> </svg>",
    sale: '<img src="https://cdn.shopify.com/s/files/1/0002/0647/1226/files/Icons_Sale_Icon_Tag_2.svg?v=1787172447" alt="" width="54" height="54" loading="lazy" decoding="async" style="width:100%;height:100%;object-fit:contain">'
  };

  // ---------- Sizes ----------
  // Whitelist of size values to show, in exact display order (matches live design).
  const ALLOWED_SIZES = [
    '12/18M', '1', '2', '3', '4', '5', '6', '7', '8', '10', '12', '14',
    'S', 'M', 'L', 'XL'
  ];

  // Raw Typesense value → canonical display label.
  const SIZE_ALIASES = {
    '12/18 MONTHS': '12/18M',
    '12/18 Months': '12/18M',
    '12-18M': '12/18M'
  };

  // Canonical label → array of raw Typesense values that should match it.
  // Used when building filter clauses so `sizes:=['12/18 Months']` also
  // matches products stored as `12/18M` or `12-18M`.
  function expandSizeToRaw(canonical) {
    const raws = [canonical];
    for (const raw in SIZE_ALIASES) {
      if (SIZE_ALIASES[raw] === canonical && !raws.includes(raw)) raws.push(raw);
    }
    return raws;
  }

  function canonicalSize(raw) {
    const s = String(raw).trim();
    if (SIZE_ALIASES[s.toUpperCase()]) return SIZE_ALIASES[s.toUpperCase()];
    // exact match (case-sensitive) against whitelist
    if (ALLOWED_SIZES.includes(s)) return s;
    // case-insensitive match
    const hit = ALLOWED_SIZES.find(a => a.toLowerCase() === s.toLowerCase());
    return hit || null;
  }

  function isAllowedSize(raw) { return canonicalSize(raw) !== null; }

  function sizeSortKey(size) {
    const idx = ALLOWED_SIZES.indexOf(canonicalSize(size) || size);
    return idx === -1 ? 9999 : idx;
  }

  // ---------- URL state (bookmarkable, shareable) ----------
  function readUrlState() {
    const p = new URLSearchParams(location.search);
    if (p.has('sort')) state.sort = p.get('sort');
    // ?sort=best-selling on a page without that option (search results) → default.
    if (state.sort === BEST_SELLING_SORT && !(el.sort && [...el.sort.options].some(o => o.value === BEST_SELLING_SORT))) {
      state.sort = DEFAULT_SORT;
    }
    if (p.has('sizes')) p.get('sizes').split(',').filter(Boolean).forEach(v => state.selected.sizes.add(v));
    if (p.has('types')) p.get('types').split(',').filter(Boolean).forEach(v => state.selected.product_type.add(v));
    el.sorts.forEach(sort => { sort.value = state.sort; });
    syncSortDisplay();
  }

  function syncSortDisplay() {
    el.sorts.forEach(sort => { sort.value = state.sort; });
    el.sortDisplays.forEach(display => {
      const sort = display.closest('[data-lts-sort-wrap]')?.querySelector('[data-lts-sort]') || el.sort;
      if (!sort) return;
      const selected = sort.options[sort.selectedIndex];
      display.textContent = selected ? selected.textContent : '';
    });
  }

  function writeUrlState() {
    const p = new URLSearchParams(location.search);
    p.delete('sizes'); p.delete('types'); p.delete('sort');
    if (state.sort !== 'published_at:desc') p.set('sort', state.sort);
    if (state.selected.sizes.size) p.set('sizes', [...state.selected.sizes].join(','));
    if (state.selected.product_type.size) p.set('types', [...state.selected.product_type].join(','));
    const qs = p.toString();
    const url = location.pathname + (qs ? '?' + qs : '') + location.hash;
    history.replaceState(null, '', url);
  }

  function getInitialSearchQuery() {
    if (CFG.isSearchPage) {
      const p = new URLSearchParams(location.search);
      return p.get('q') || '';
    }
    return '';
  }

  // ---------- Typesense query builder ----------
  // Strip plural endings so plural handle segments match singular product_type
  // values (Shopify convention). Rules follow standard English plural forms:
  //   categories → category  (ies → y)
  //   watches    → watch     (ches → ch)
  //   boxes      → box       (xes → x)
  //   dresses    → dress     (sses → ss)
  //   shoes      → shoe      (s only — base "shoe" doesn't take "es")
  //   tops       → top       (s only)
  function stem(word) {
    let w = String(word || '').trim();
    if (w.length <= 3) return w;
    if (/ies$/i.test(w)) return w.replace(/ies$/i, 'y');
    if (/(ches|shes|xes|sses)$/i.test(w)) return w.replace(/es$/i, '');
    if (/ss$/i.test(w)) return w;
    if (/s$/i.test(w)) return w.replace(/s$/i, '');
    return w;
  }

  // Build the base scope clause — which products belong to this collection page.
  // 1st attempt: real collection_handles match.
  // Fallback: tag + optional product_type substring (handle-parsed).
  //
  // IMPORTANT: when the user has picked a category filter, we DROP the
  // fallback product_type constraint. Otherwise the scope's inferred type
  // (e.g. dress on /collections/girls-dresses-1) would AND-conflict with
  // the user's chosen category (e.g. tops), yielding zero results.
  function buildScopeClause(useTagFallback) {
    if (CFG.isSearchPage) return '';
    if (useTagFallback && CFG.fallbackTag) {
      const parts = [`tags:=${escapeValue(CFG.fallbackTag)}`];
      // Only a REAL category (not Sale — which is orthogonal to type) drops
      // the fallback type. Sale within /girls-dresses-1 = sale dresses, not
      // "all sale Girls products across all types".
      const cats = [...CATEGORIES.kids, ...CATEGORIES.adults];
      const userPickedRealCategory = [...state.selected.product_type].some(k => {
        const c = cats.find(x => x.key === k);
        return c && !c.isSale;
      });
      if (CFG.fallbackType && !userPickedRealCategory) {
        const t = escapeValue(stem(CFG.fallbackType)).replace(/`/g, '');
        parts.push(`product_type:*${t}*`);
      }
      return parts.join(' && ');
    }
    if (CFG.collectionHandle) {
      return `collection_handles:=${escapeValue(CFG.collectionHandle)}`;
    }
    return '';
  }

  function buildFilterBy(useTagFallback, opts) {
    opts = opts || {};
    const clauses = [];

    const scope = buildScopeClause(useTagFallback);
    if (scope) clauses.push(scope);

    // Strict in-stock: `available` alone is unreliable (Shopify flags true
    // for continue-selling-when-oos + untracked inventory). Combine with
    // actual inventory count so genuinely sold-out products are hidden.
    if (CFG.hideOutOfStock) {
      clauses.push('available:=true');
      clauses.push('total_inventory:>0');
    }

    // Only show products actually published to the online store. Unpublished
    // / draft products have publishedAt == null (stored as 0). This is the
    // most common cause of "sold-out looking" items surfacing — the product
    // is inventory-in-stock but not published to the storefront.
    clauses.push('published_at:>0');

    // Filter out free-gift / service items (price = 0) and merchant-hidden
    // products (tag `hide-from-search`).
    clauses.push('price:>0');
    clauses.push('tags:!=`hide-from-search`');

    if (CFG.excludeVendors && CFG.excludeVendors.length) {
      CFG.excludeVendors.forEach(v => clauses.push(`vendor:!=${escapeValue(v)}`));
    }
    if (CFG.excludeTypes && CFG.excludeTypes.length) {
      CFG.excludeTypes.forEach(t => clauses.push(`product_type:!=${escapeValue(t)}`));
    }
    // Merchant-excluded tags (e.g. customizer patch add-ons tagged `patch`):
    // never surface on collection or search results. Tag filters are
    // case-sensitive in Typesense, so every casing is listed in CFG.
    if (CFG.excludeTags && CFG.excludeTags.length) {
      CFG.excludeTags.forEach(t => clauses.push(`tags:!=${escapeValue(t)}`));
    }

    // opts.skipUserFilters: build the scope+safety filters ONLY, no user
    // facet selections. Used by the facet-fetch path so pill counts reflect
    // the collection's actual assortment, not the currently-filtered subset
    // (otherwise picking a size that hits 0 products blanks the whole row
    // and the user can't switch to another size).
    if (opts.skipUserFilters) return clauses.join(' && ');

    if (state.selected.sizes.size) {
      // Expand each canonical size to all raw variants stored in Typesense.
      const raws = [];
      state.selected.sizes.forEach(canonical => {
        expandSizeToRaw(canonical).forEach(r => raws.push(r));
      });
      const vals = raws.map(escapeValue).join(',');
      // When hideOutOfStock is on, match against sizes_available (only sizes
      // whose variant is actually purchasable). Otherwise fall back to sizes.
      const field = CFG.hideOutOfStock ? 'sizes_available' : 'sizes';
      clauses.push(`${field}:=[${vals}]`);
    }

    // Category keys map to a set of product_type substring matches.
    // A product has ONE product_type, so multi-select must be OR — a
    // product matches if it belongs to ANY of the selected categories.
    // Special: the "Sale" category has isSale=true and filters on the
    // on_sale field (products with compare_at > price) instead of type.
    if (state.selected.product_type.size) {
      const cats = [...CATEGORIES.kids, ...CATEGORIES.adults];
      const typeClauses = [];
      let saleSelected = false;
      for (const key of state.selected.product_type) {
        const cat = cats.find(c => c.key === key);
        if (!cat) continue;
        if (cat.isSale) { saleSelected = true; continue; }
        cat.matches.forEach(m => {
          const esc = escapeValue(m).replace(/`/g, '');
          typeClauses.push(`product_type:*${esc}*`);
        });
      }
      if (typeClauses.length && saleSelected) {
        // User picked a real category AND Sale — OR them: (matches types) OR on_sale
        clauses.push(`((${typeClauses.join(' || ')}) || on_sale:=true)`);
      } else if (typeClauses.length) {
        clauses.push(`(${typeClauses.join(' || ')})`);
      } else if (saleSelected) {
        clauses.push(`on_sale:=true`);
      }
    }

    return clauses.join(' && ');
  }

  function escapeValue(v) {
    // Typesense expects backtick-quoted values that contain spaces or special chars
    if (/[\s,`&|=<>!()\[\]]/.test(v)) return '`' + String(v).replace(/`/g, '\\`') + '`';
    return v;
  }

  function buildSearchParams(page, useTagFallback, opts) {
    opts = opts || {};
    const params = new URLSearchParams();
    const q = CFG.isSearchPage ? (state.query || '*') : '*';
    params.set('q', q);
    // vendor left out on purpose: most products have vendor "Lola + The Boys", so a "boys" search matched girls items too
    params.set('query_by', 'title,product_type,tags');
    params.set('filter_by', buildFilterBy(useTagFallback, opts));
    // Typesense cannot sort by best selling; if Shopify's order could not be
    // loaded we land here and use the default sort instead of an invalid one.
    params.set('sort_by', state.sort === BEST_SELLING_SORT ? DEFAULT_SORT : state.sort);
    params.set('per_page', opts.facetOnly ? 1 : CFG.perPage);
    params.set('page', page);
    if (opts.facetOnly || !state.facetsInitialized) {
      params.set('facet_by', 'sizes_available,product_type');
      params.set('max_facet_values', '100');
    }
    return params;
  }

  async function typesenseSearch(page, useTagFallback, opts) {
    const url = `${CFG.host}/collections/${CFG.collectionName}/documents/search?` + buildSearchParams(page, useTagFallback, opts);
    const r = await fetch(url, { headers: { 'X-TYPESENSE-API-KEY': CFG.apiKey } });
    if (!r.ok) throw new Error('search ' + r.status);
    return r.json();
  }

  // Fetch a page of Shopify collection product IDs in the merchant-set order
  // (250 max per request). Returns [] on virtual URLs where the collection
  // doesn't exist as a Shopify resource.
  async function fetchShopifyOrder(handle, page, limit) {
    const per = limit || CFG.perPage;
    // One retry on 429 (Shopify's per-IP limiter) — parallel page preloads can
    // occasionally trip it; a short backoff almost always clears it.
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const r = await fetch(`/collections/${handle}/products.json?limit=${per}&page=${page}`, {
          headers: { 'Accept': 'application/json' }
        });
        if (r.status === 429 && attempt === 0) { await new Promise(res => setTimeout(res, 1200 + page * 150)); continue; }
        if (!r.ok) return null;
        const j = await r.json();
        return (j.products || []).map(p => String(p.id));
      } catch { return null; }
    }
    return null;
  }

  // Preload merchant-order IDs at boot so filter clicks don't wait for Shopify.
  // Pages are fetched in PARALLEL (Liquid told us the product count), up to
  // MERCHANT_MAX_PAGES × 250. `complete` records whether we hold the WHOLE
  // collection order — filtered merchant-order is only safe when it does,
  // otherwise products past the cap would silently vanish under a filter.
  // (There is no 1000-id cap on products.json; the real limit is Shopify's
  // rate limiter — sequential page 9+ returns 429.)
  const MERCHANT_MAX_PAGES = 6;  // 1500 ids
  async function preloadMerchantIds(handle) {
    if (!handle || state.merchantIdsCache?.handle === handle) return;
    state.merchantIdsCache = { handle, ids: null, complete: false, promise: null };
    state.merchantIdsCache.promise = (async () => {
      const expected = Number(CFG.initialCount) || 0;
      const pages = Math.max(1, Math.min(MERCHANT_MAX_PAGES,
        expected ? Math.ceil(expected / 250) : MERCHANT_MAX_PAGES));
      const batches = await Promise.all(
        Array.from({ length: pages }, (_, i) => fetchShopifyOrder(handle, i + 1, 250))
      );
      const all = [];
      let complete = false;
      for (let i = 0; i < batches.length; i++) {
        const b = batches[i];
        if (!b) break;                                  // a page failed → unknown past here
        all.push(...b);
        if (b.length < 250) { complete = true; break; } // short page = end of collection
      }
      if (expected && all.length >= expected) complete = true;
      state.merchantIdsCache.ids = all;
      state.merchantIdsCache.complete = complete;
      return all;
    })();
    return state.merchantIdsCache.promise;
  }

  // Returns CFG.perPage merchant-ordered IDs that pass current filters.
  // Uses the preloaded ID cache to avoid Shopify round-trips on every click.
  async function fetchMerchantOrderIds(handle, page, hasFacetFilters) {
    // Kick off (or await) preload — takes ~1s once, then instant thereafter.
    if (!state.merchantIdsCache || state.merchantIdsCache.handle !== handle) {
      preloadMerchantIds(handle);
    }
    const allIds = state.merchantIdsCache?.ids
      || await state.merchantIdsCache?.promise
      || [];
    if (!allIds.length) return null;

    const start = (page - 1) * CFG.perPage;

    // No filters — direct slice from preloaded order (instant).
    if (!hasFacetFilters) return allIds.slice(start, start + CFG.perPage);

    // Filters active — cache the filtered ID sequence per filter set.
    if (!state.filteredOrderCache) state.filteredOrderCache = { key: null, ids: [] };
    const cacheKey = handle + '|' + [...state.selected.sizes].sort().join(',') + '||' + [...state.selected.product_type].sort().join(',');
    if (state.filteredOrderCache.key !== cacheKey) {
      state.filteredOrderCache = { key: cacheKey, ids: await typesenseFilterIds(allIds) };
    }
    return state.filteredOrderCache.ids.slice(start, start + CFG.perPage);
  }

  // Given a list of product IDs (in some order), return the SUBSET that
  // matches the current user facet filters, PRESERVING the input order.
  async function typesenseFilterIds(ids) {
    if (!ids.length) return [];
    // Only user facet filters + baseline filters (in-stock, published, etc.)
    // Skip scope clauses (collection_handles / tags fallback) since we already
    // constrained to Shopify's collection membership via the ID list itself.
    const extra = buildFilterBy(false).split(' && ').filter(c =>
      c && !c.startsWith('collection_handles:') && !c.startsWith('tags:=`') && !c.startsWith('tags:=Girls') && !/^tags:=[A-Z]/.test(c)
    );
    const filter = [`id:=[${ids.join(',')}]`, ...extra].join(' && ');
    // Typesense caps per_page at 250. For large collections we paginate.
    const PAGE = 250;
    const totalPages = Math.max(1, Math.ceil(ids.length / PAGE));
    // POST /multi_search: one query per page. Sidesteps GET's 4000-char URL
    // cap AND Typesense's 250-hit-per-page limit in a single round-trip.
    const searches = Array.from({ length: totalPages }, (_, i) => ({
      collection: CFG.collectionName,
      q: '*', query_by: 'title', filter_by: filter,
      per_page: PAGE, page: i + 1, include_fields: 'id'
    }));
    const url = `${CFG.host}/multi_search?x-typesense-api-key=${encodeURIComponent(CFG.apiKey)}`;
    const r = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify({ searches })
    });
    if (!r.ok) return [];
    const wrapper = await r.json();
    const allowed = new Set();
    (wrapper.results || []).forEach(res => {
      (res.hits || []).forEach(h => allowed.add(String(h.document.id)));
    });
    return ids.filter(id => allowed.has(String(id)));
  }

  // Given an ordered list of IDs, fetch full docs from Typesense (with any
  // active facet filters applied) and re-order the results to match.
  async function typesenseByIds(ids, page, opts) {
    opts = opts || {};
    const idFilter = `id:=[${ids.join(',')}]`;
    // opts.skipUserFilters strips size/type clauses so facet counts reflect
    // the whole collection (not the currently-filtered subset) — otherwise
    // a size that returns 0 products would blank the entire pill row.
    const extra = buildFilterBy(false, opts).split(' && ').filter(c => c && !c.startsWith('collection_handles:') && !c.startsWith('tags:='));
    const filter = [idFilter, ...extra].join(' && ');
    const wantFacet = opts.facetOnly || !state.facetsInitialized;

    // Use POST /multi_search — the GET endpoint's 4000-char URL cap breaks
    // as soon as we pass ~200 IDs (a large collection's merchant order).
    const searchBody = {
      searches: [{
        collection: CFG.collectionName,
        q: '*',
        query_by: 'title',
        filter_by: filter,
        per_page: opts.facetOnly ? 1 : Math.min(250, ids.length),
        page: 1,
      }]
    };
    if (wantFacet) {
      searchBody.searches[0].facet_by = 'sizes_available,product_type';
      searchBody.searches[0].max_facet_values = 100;
    }
    const url = `${CFG.host}/multi_search?x-typesense-api-key=${encodeURIComponent(CFG.apiKey)}`;
    const r = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify(searchBody)
    });
    if (!r.ok) throw new Error('idsearch ' + r.status);
    const wrapper = await r.json();
    const data = (wrapper.results && wrapper.results[0]) || {};

    // Preserve merchant sort order
    const hits = Array.isArray(data.hits) ? data.hits : [];
    const idMap = new Map(hits.map(h => [String(h.document.id), h]));
    const ordered = ids.map(id => idMap.get(String(id))).filter(Boolean);
    data.hits = ordered;
    return data;
  }

  // ---------- Best selling (Shopify's order) ----------
  // products.json ignores sort_by, so the order comes from one of two places,
  // both 250 ids per request and both in the same order:
  //   1. Storefront API (no token needed), about 0.3 s per request;
  //   2. the Liquid view templates/collection.lts-order.liquid, 2-5 s per
  //      request, used only when the API does not answer.
  const STOREFRONT_API = '/api/2025-07/graphql.json';
  // @inContext: the API answers for the US market unless told the shopper's
  // country, while the storefront lists what is sold in THEIR market (a few
  // products are US-only). Passing Shopify.country keeps the two identical.
  const BEST_SELLING_QUERY = 'query($h:String!,$a:String,$c:CountryCode)@inContext(country:$c){collection(handle:$h){products(first:250,after:$a,sortKey:BEST_SELLING){nodes{id}pageInfo{hasNextPage endCursor}}}}';
  function shopperCountry() {
    const c = window.Shopify && window.Shopify.country;
    return typeof c === 'string' && /^[A-Z]{2}$/.test(c) ? c : null;
  }

  // 250 ids after `cursor` from the Storefront API.
  // Returns { ids, next } (next = cursor of the following page, null at the
  // end), or null when the API cannot be used.
  async function fetchBestSellingApi(handle, cursor) {
    for (let attempt = 0; attempt < 2; attempt++) {
      if (attempt) await new Promise(res => setTimeout(res, 500));
      try {
        const r = await fetch(STOREFRONT_API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: BEST_SELLING_QUERY, variables: { h: handle, a: cursor || null, c: shopperCountry() } })
        });
        if (r.status === 429 || r.status >= 500) continue;
        if (!r.ok) return null;
        const j = await r.json();
        const products = j && j.data && j.data.collection && j.data.collection.products;
        if (!products || !Array.isArray(products.nodes)) return null;
        const info = products.pageInfo || {};
        return {
          ids: products.nodes.map(n => String(n.id).split('/').pop()),
          next: info.hasNextPage && info.endCursor ? info.endCursor : null
        };
      } catch (e) { /* try again */ }
    }
    return null;
  }

  // The same page from the Liquid view (page N = the Nth 250 ids).
  // Returns { ids, pages }, or null when the page could not be read.
  async function fetchBestSellingPage(handle, page) {
    const url = `/collections/${encodeURIComponent(handle)}?view=lts-order&sort_by=best-selling&page=${page}`;
    // Three tries (429 from Shopify's limiter, a 5xx, a dropped connection),
    // with a short pause before the second and third.
    for (let attempt = 0; attempt < 3; attempt++) {
      if (attempt) await new Promise(res => setTimeout(res, 600 * attempt));
      try {
        const r = await fetch(url, { credentials: 'same-origin' });
        if (r.status === 429 || r.status >= 500) continue;
        if (!r.ok) return null;
        const j = JSON.parse((await r.text()).trim());
        if (!j || !Array.isArray(j.ids)) return null;
        return { ids: j.ids.map(String), pages: Number(j.pages) || 1 };
      } catch (e) { /* try again */ }
    }
    return null;
  }

  // The collection in best-selling order, 250 ids per request, kept for the
  // whole page view. shop-all is 11 requests, so the first 250 ids go on
  // screen straight away and the rest loads in the background.
  const BEST_SELLING_MAX_PAGES = 24;  // 6000 products
  function bestSellingCacheFor(handle) {
    let c = state.bestSellingCache;
    // After a failure wait a minute before asking Shopify again, so every
    // scroll does not sit through the same retries.
    const retry = c && c.failed && Date.now() >= c.retryAt;
    if (!c || c.handle !== handle || retry) {
      c = state.bestSellingCache = {
        handle, ids: [], seen: new Set(), nextPage: 1, cursor: null, apiDown: false,
        done: false, failed: false, retryAt: 0, loading: null
      };
    }
    return c;
  }

  // Loads the next 250 ids. Calls made while one is running share it.
  function loadMoreBestSelling(c) {
    if (c.done || c.failed) return Promise.resolve();
    if (c.loading) return c.loading;
    c.loading = (async () => {
      let ids = null;
      let last = false;
      if (!c.apiDown) {
        const api = await fetchBestSellingApi(c.handle, c.cursor);
        if (api) { ids = api.ids; last = !api.next; c.cursor = api.next; }
        else c.apiDown = true;  // rest of this list comes from the Liquid view
      }
      if (!ids) {
        const view = await fetchBestSellingPage(c.handle, c.nextPage);
        if (view) { ids = view.ids; last = c.nextPage >= view.pages; }
      }
      if (!ids) { c.failed = true; c.retryAt = Date.now() + 60000; return; }
      ids.forEach(id => { if (!c.seen.has(id)) { c.seen.add(id); c.ids.push(id); } });
      c.nextPage++;
      if (last || !ids.length || c.nextPage > BEST_SELLING_MAX_PAGES) c.done = true;
    })().finally(() => { c.loading = null; });
    return c.loading;
  }

  // POST one multi_search and return its results array (throws on any error).
  async function typesenseMulti(searches) {
    const url = `${CFG.host}/multi_search?x-typesense-api-key=${encodeURIComponent(CFG.apiKey)}`;
    const r = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify({ searches })
    });
    if (!r.ok) throw new Error('multi ' + r.status);
    const results = (await r.json()).results || [];
    if (results.length !== searches.length || results.some(res => !res || res.error)) throw new Error('multi result');
    return results;
  }

  // Baseline filters (in stock, published, exclusions) plus, unless
  // skipUserFilters, the shopper's size/category picks — without the scope
  // clause, because an id list already says which products belong.
  function idListFilters(opts) {
    return buildFilterBy(false, opts).split(' && ')
      .filter(c => c && !c.startsWith('collection_handles:') && !c.startsWith('tags:='));
  }

  // Keep only the ids that pass the current filters, in the same order.
  // One Typesense query per 250 ids, so the request stays small even for
  // shop-all (typesenseFilterIds repeats the whole id list in every query).
  async function filterIdsInOrder(ids) {
    if (!ids.length) return [];
    const extra = idListFilters();
    const searches = [];
    for (let i = 0; i < ids.length; i += 250) {
      searches.push({
        collection: CFG.collectionName, q: '*', query_by: 'title',
        filter_by: [`id:=[${ids.slice(i, i + 250).join(',')}]`, ...extra].join(' && '),
        per_page: 250, page: 1, include_fields: 'id'
      });
    }
    const allowed = new Set();
    for (let i = 0; i < searches.length; i += 20) {
      (await typesenseMulti(searches.slice(i, i + 20))).forEach(res => {
        (res.hits || []).forEach(h => allowed.add(String(h.document.id)));
      });
    }
    return ids.filter(id => allowed.has(String(id)));
  }

  // Size / category facet counts over an id list, ignoring the shopper's own
  // filters. Only used when the collection-wide facet query finds nothing.
  async function facetsForIds(ids) {
    const extra = idListFilters({ skipUserFilters: true });
    const searches = [];
    for (let i = 0; i < ids.length && searches.length < 20; i += 250) {
      searches.push({
        collection: CFG.collectionName, q: '*', query_by: 'title',
        filter_by: [`id:=[${ids.slice(i, i + 250).join(',')}]`, ...extra].join(' && '),
        per_page: 1, page: 1, include_fields: 'id',
        facet_by: 'sizes_available,product_type', max_facet_values: 100
      });
    }
    if (!searches.length) return null;
    const merged = new Map();
    (await typesenseMulti(searches)).forEach(res => {
      (res.facet_counts || []).forEach(f => {
        if (!merged.has(f.field_name)) merged.set(f.field_name, new Map());
        const m = merged.get(f.field_name);
        (f.counts || []).forEach(c => m.set(c.value, (m.get(c.value) || 0) + c.count));
      });
    });
    return [...merged].map(([field_name, m]) => ({
      field_name,
      counts: [...m].map(([value, count]) => ({ value, count }))
    }));
  }

  // ---- best-selling list for ONE filter set ----
  // entry.filtered = the ids loaded so far that are in stock and pass the
  // shopper's filters, still in best-selling order. A single background loop
  // per entry keeps extending it until the whole collection has been read;
  // page requests just wait until enough ids are there.
  function bestFilterKey() {
    return [...state.selected.sizes].sort().join(',') + '||' + [...state.selected.product_type].sort().join(',');
  }

  function bestEntryNotify(entry) {
    const waiters = entry.waiters;
    entry.waiters = [];
    waiters.forEach(fn => fn());
  }

  function bestEntryWait(entry, need) {
    return new Promise(resolve => {
      const check = () => {
        if (entry.filtered.length >= need || entry.finished || !entry.running) resolve();
        else entry.waiters.push(check);
      };
      check();
    });
  }

  async function runBestEntry(entry) {
    if (entry.running || entry.finished) return;
    entry.running = true;
    const c = entry.cache;
    try {
      for (;;) {
        // The shopper moved on (other sort / other filters) → stop loading.
        if (state.bestFilteredCache !== entry || state.sort !== BEST_SELLING_SORT) break;
        if (entry.upto < c.ids.length) {
          const chunk = c.ids.slice(entry.upto);
          const kept = await filterIdsInOrder(chunk);
          entry.filtered.push(...kept);
          entry.upto += chunk.length;
          bestEntryNotify(entry);
          continue;
        }
        if (c.done || c.failed) { entry.finished = true; break; }
        await loadMoreBestSelling(c);
      }
    } catch (e) {
      entry.finished = true;  // Typesense error: stop with what we have
    }
    entry.running = false;
    bestEntryNotify(entry);
    if (entry.finished) settleBestSellingTotal(entry);
  }

  // The whole collection has been read: the "N STYLES" count is now exact.
  function settleBestSellingTotal(entry) {
    if (state.bestFilteredCache !== entry || state.sort !== BEST_SELLING_SORT || entry.key !== bestFilterKey()) return;
    if (!entry.rendered) return;  // page 1 not on screen yet; it will carry the exact total itself
    state.total = entry.filtered.length;
    if (el.count) el.count.textContent = state.total.toLocaleString() + ' STYLES';
    const inGrid = el.grid ? el.grid.querySelectorAll('.lts-card:not(.lts-card--skeleton)').length : 0;
    state.exhausted = inGrid >= state.total;
  }

  // Start reading Shopify's order as soon as the shopper opens the sort
  // menu, so picking BEST SELLING does not start from zero.
  function prewarmBestSelling() {
    if (CFG.isSearchPage || !CFG.collectionHandle) return;
    if (!(el.sort && [...el.sort.options].some(o => o.value === BEST_SELLING_SORT))) return;
    const c = bestSellingCacheFor(CFG.collectionHandle);
    if (!c.ids.length) loadMoreBestSelling(c);
  }

  // One page of the collection in best-selling order. Returns null when
  // Shopify's order could not be loaded; search() then uses the default sort.
  async function searchBestSelling(page) {
    const c = bestSellingCacheFor(CFG.collectionHandle);
    if (c.failed && !c.ids.length) return null;  // Shopify did not answer a moment ago
    const key = bestFilterKey();
    let entry = state.bestFilteredCache;
    if (!entry || entry.key !== key || entry.cache !== c) {
      entry = state.bestFilteredCache = {
        key, cache: c, filtered: [], upto: 0, waiters: [],
        running: false, finished: false, rendered: false, estimate: 0
      };
    }
    const hasFacetFilters = state.selected.sizes.size || state.selected.product_type.size;
    const start = (page - 1) * CFG.perPage;
    runBestEntry(entry);
    await bestEntryWait(entry, start + CFG.perPage);
    if (!c.ids.length) return null;  // Shopify never answered
    // Up to 500 products is one more quick request: read it all before the
    // first paint so the count is exact from the start.
    const shopifyCount = Number(CFG.initialCount) || 0;
    if (page === 1 && !entry.finished && shopifyCount && shopifyCount <= 500) await bestEntryWait(entry, Infinity);

    const ids = entry.filtered.slice(start, start + CFG.perPage);
    if (!ids.length) return { hits: [], found: entry.filtered.length, facet_counts: [] };

    const useful = fc => Array.isArray(fc) && fc.some(f => f.counts && f.counts.length);
    const [data, tot] = await Promise.all([
      typesenseByIds(ids, page),
      page === 1 ? fetchFacetsAndTotal().catch(() => null) : Promise.resolve(null)
    ]);

    if (page === 1) {
      // typesenseByIds only saw this page's ids, so its facets are partial.
      // Replace them with the collection-wide ones (or drop them) so the
      // size/category rows are never frozen from 40 products.
      let facets = null;
      if (tot && useful(tot.facet_counts)) facets = tot.facet_counts;
      else {
        const fromIds = await facetsForIds(c.ids).catch(() => null);
        if (useful(fromIds)) facets = fromIds;
      }
      if (facets) data.facet_counts = facets; else delete data.facet_counts;

      // Until the whole collection is read the exact total is unknown;
      // Typesense's count for the same filters is a close stand-in.
      if (!entry.finished) {
        if (!hasFacetFilters && tot && tot.found) entry.estimate = tot.found;
        else {
          const est = await typesenseSearch(1, state.usingTagFallback, { facetOnly: true }).catch(() => null);
          entry.estimate = (est && est.found) || 0;
        }
      }
      entry.rendered = true;
    } else {
      delete data.facet_counts;
    }

    // Exact once everything is read. Before that: never below what is
    // already known, and always above it so the list is not ended early.
    data.found = entry.finished
      ? entry.filtered.length
      : Math.max(entry.filtered.length + 1, entry.estimate || 0);
    return data;
  }

  // SSR page-1 cards come from Liquid, which can't see Typesense's
  // colors_available — so they render without swatches while JS-rendered
  // pages get them. Fetch the docs for the SSR ids and inject the exact
  // markup productCardHTML() emits, then align the hero image with the
  // first in-stock color (same rule as JS cards) so the active dot matches.
  async function hydrateSsrSwatches() {
    const ids = state.ssrIds ? [...state.ssrIds] : [];
    if (!ids.length || !el.grid) return;
    let res = null;
    try { res = await typesenseByIds(ids, 1, { skipUserFilters: true }); } catch (e) { return; }
    const base = u => String(u || '').split('?')[0].split('/').pop();
    ((res && res.hits) || []).forEach(h => {
      const doc = h.document;
      if (!doc || !doc.colors_available) return;
      const card = el.grid.querySelector(`.lts-card[data-product-id="${doc.id}"]`);
      if (!card || card.querySelector('.lts-card__swatches')) return;
      const html = renderSwatches(doc);
      if (!html) return;
      const body = card.querySelector('.lts-card__body');
      if (!body) return;
      body.insertAdjacentHTML('afterbegin', html);

      let first = null;
      try { first = (JSON.parse(doc.colors_available) || []).find(c => c && c.in_stock) || null; } catch (e) {}
      if (!first || !first.image_url) return;
      const img = card.querySelector('.lts-card__img:not(.lts-card__img--hover)');
      if (!img) return;
      if (img.hasAttribute('data-lb-src')) {
        // Still waiting for the page to load (lbLoadDeferredImages): queue this colour's picture instead of fetching it now.
        img.setAttribute('data-lb-src', cdnResize(first.image_url, 400, 'webp'));
        img.setAttribute('data-lb-srcset', buildSrcset(first.image_url));
        const waitingHover = card.querySelector('.lts-card__img--hover[data-lb-src]');
        if (waitingHover && first.hover_image_url && first.hover_image_url !== first.image_url) {
          waitingHover.setAttribute('data-lb-src', cdnResize(first.hover_image_url, 400, 'webp'));
        }
        return;
      }
      if (base(img.getAttribute('src')) === base(first.image_url)) return;
      // Preload, then swap — avoids a blank flash on an already-painted LCP image.
      const next = cdnResize(first.image_url, 400, 'webp');
      const pre = new Image();
      pre.onload = () => {
        img.srcset = buildSrcset(first.image_url);
        img.src = next;
        img.dataset.loaded = 'true';
        const hover = card.querySelector('.lts-card__img--hover');
        if (hover && first.hover_image_url && first.hover_image_url !== first.image_url) {
          hover.removeAttribute('srcset');
          hover.src = cdnResize(first.hover_image_url, 400, 'webp');
        }
      };
      pre.src = next;
    });
  }

  // Runs a facet + total-count only query using the widest scope that has
  // products for this collection. Tries the real handle first, falls back
  // to the tag scope if the handle returns nothing.
  async function fetchFacetsAndTotal() {
    // Facet fetch: skip user filters so pill counts always reflect the
    // collection's assortment, not the currently-filtered subset.
    // Otherwise a size selection that hits 0 products blanks the whole row.
    const opts = { facetOnly: true, skipUserFilters: true };
    let data = await typesenseSearch(1, false, opts);
    if (data.found === 0 && !CFG.isSearchPage && CFG.fallbackTag) {
      const fb = await typesenseSearch(1, true, opts);
      if (fb.found > 0) {
        state.usingTagFallback = true;
        return fb;
      }
    }
    // Neither collection_handles nor tag+type scope matched — happens on
    // curated collections whose products aren't tagged with the handle-
    // parsed fallback (e.g. sale-on-sale → tag=Sale + type=`on`). Use
    // Shopify's own collection membership by id.
    if (data.found === 0 && !CFG.isSearchPage && CFG.collectionHandle) {
      const ids = await fetchMerchantOrderIds(CFG.collectionHandle, 1, false).catch(() => null);
      if (ids && ids.length) {
        const idRes = await typesenseByIds(ids, 1, opts).catch(() => null);
        if (idRes && idRes.found) return idRes;
      }
    }
    return data;
  }

  async function search(page) {
    const hasFacetFilters = state.selected.sizes.size || state.selected.product_type.size;
    // Best selling: Shopify's own order, sold-out products removed.
    if (state.sort === BEST_SELLING_SORT && !CFG.isSearchPage && CFG.collectionHandle) {
      if (page === 1) state.bestSellingFallback = false;
      if (!state.bestSellingFallback) {
        let best = null;
        // A failed later page is thrown on, like any other failed page: the
        // list stays as it is and the next scroll tries again.
        try { best = await searchBestSelling(page); } catch (e) { if (page > 1) throw e; }
        // usingMerchantOrder stays false: every best-selling page carries the
        // current total, and renderResults should take it each time.
        if (best) { state.usingMerchantOrder = false; return best; }
        // Lost the order part-way down the list: end it here rather than
        // append default-sorted products the shopper has already seen.
        if (page > 1) return { hits: [], found: state.total || 0, facet_counts: [] };
        // Shopify did not answer for page 1 → whole list in the default sort.
        state.bestSellingFallback = true;
        console.warn('[LATB] best-selling order unavailable, using default sort');
      }
    }
    // Use merchant order for the default "Featured" sort. Skip it when
    // filters are ACTIVE because Shopify's Storefront `products.json`
    // caps a collection at 1000 IDs; filtering those 1000 through
    // Typesense would silently drop every match beyond the first 1000
    // (e.g. shop-all has 2 584 products → only 20 baskets total, of which
    // just 1 fell in the first 1000 → user saw 1 basket instead of 20).
    // With filters we keep the merchant's manual order too — but ONLY when we
    // hold the COMPLETE id list (see preloadMerchantIds). Oversized
    // collections fall back to Typesense's own sort so no product is lost.
    let merchantComplete = false;
    if (hasFacetFilters && !CFG.isSearchPage && state.sort === 'published_at:desc' && CFG.collectionHandle) {
      if (!state.merchantIdsCache || state.merchantIdsCache.handle !== CFG.collectionHandle) {
        preloadMerchantIds(CFG.collectionHandle);
      }
      try { if (!state.merchantIdsCache.ids) await state.merchantIdsCache.promise; } catch (e) {}
      merchantComplete = !!(state.merchantIdsCache && state.merchantIdsCache.complete);
    }
    const wantMerchantOrder = !CFG.isSearchPage
      && state.sort === 'published_at:desc'
      && (!hasFacetFilters || merchantComplete);

    // Merchant order path: use Shopify's collection sort_order.
    if (wantMerchantOrder && CFG.collectionHandle) {
      const ids = await fetchMerchantOrderIds(CFG.collectionHandle, page, hasFacetFilters);
      if (ids && ids.length) {
        state.usingMerchantOrder = true;
        const data = await typesenseByIds(ids, page);
        // Under filters the authoritative total is the filtered merchant list;
        // typesenseByIds only knows this page's ≤40 ids.
        if (hasFacetFilters && state.filteredOrderCache && state.filteredOrderCache.ids) {
          data.found = state.filteredOrderCache.ids.length;
        }
        // On first page, run a parallel facet+total query so the UI shows
        // the true collection-wide count and facet options (not just the
        // 40 IDs we requested by id).
        if (page === 1) {
          // Only run the extra facet+total query when we DON'T already have
          // useful facets from typesenseByIds. Otherwise the extra call is
          // wasted work and, on collections whose scope+fallback both miss,
          // it may hang chasing empty scopes while blocking the render.
          const hasRealFacets = Array.isArray(data.facet_counts)
            && data.facet_counts.some(f => f.counts && f.counts.length);
          if (!hasRealFacets) {
            const tot = await fetchFacetsAndTotal().catch(() => null);
            if (tot) {
              // Collection-wide total only applies when no filters are active.
              if (!hasFacetFilters) data.found = tot.found || data.hits.length;
              const totFacetsUseful = Array.isArray(tot.facet_counts)
                && tot.facet_counts.some(f => f.counts && f.counts.length);
              if (totFacetsUseful) data.facet_counts = tot.facet_counts;
            }
          }
          // Without filters `found` is still this page's id query (≤40), which
          // would end the list after one page when the shopper comes back to
          // Featured from another sort or clears the filters. Use the same
          // total as the first paint: Shopify's count for the collection.
          if (!hasFacetFilters) {
            const whole = Number(CFG.initialCount)
              || (state.merchantIdsCache && state.merchantIdsCache.ids ? state.merchantIdsCache.ids.length : 0);
            if (whole > data.found) data.found = whole;
          }
        }
        return data;
      }
      // We hold the complete merchant list and this page has no ids left →
      // end of results (or no match at all). Return empty instead of falling
      // through to the Typesense-sorted path, which would append products
      // in a different order (and re-show ones already rendered).
      if (state.merchantIdsCache && state.merchantIdsCache.complete && state.merchantIdsCache.ids && state.merchantIdsCache.ids.length) {
        state.usingMerchantOrder = true;
        const total = hasFacetFilters
          ? ((state.filteredOrderCache && state.filteredOrderCache.ids) ? state.filteredOrderCache.ids.length : 0)
          : state.merchantIdsCache.ids.length;
        return { hits: [], found: total, facet_counts: [] };
      }
    }
    state.usingMerchantOrder = false;

    // Standard Typesense path (sort by price/date OR filters active OR virtual handle).
    // Once we've determined this is a virtual handle (needs tag fallback), skip
    // the wasted primary query on every subsequent request — save ~300ms/click.
    if (state.usingTagFallback && CFG.fallbackTag) {
      return typesenseSearch(page, true);
    }
    let data = await typesenseSearch(page, false);
    if (page === 1 && data.found === 0 && !CFG.isSearchPage && CFG.fallbackTag) {
      state.usingTagFallback = true;
      data = await typesenseSearch(page, true);
    }
    return data;
  }

  // ---------- Rendering ----------
  function formatPrice(p) {
    if (typeof p !== 'number' || isNaN(p)) return '';
    return '$' + p.toFixed(2);
  }

  // ---------- Sale prices ----------
  // The store's automatic % discounts (e.g. Friends & Family 20%) only apply in
  // the cart, so cards show them from CFG.sales: the shop metafield
  // lola.active_sales, written by the Hetzner script lola-sales.mjs. Shopify
  // may serve that from cache, so window.LolaSales (snippets/lola-sales-live)
  // loads the server's fresh copy and setSales() + refreshSalePrices() apply it.
  // Each sale has mode all / include (only ids) / exclude (all but ids). A
  // product gets the highest percent among its sales.
  let SALES = [];
  function setSales(list) {
    SALES = (Array.isArray(list) ? list : [])
      .map(s => ({ ...s, idSet: new Set((s.ids || []).map(String)) }));
  }
  setSales(CFG.sales);

  function salePercent(id) {
    if (!SALES.length) return 0;
    const now = Date.now() / 1000;
    const key = String(id);
    let best = 0;
    for (const s of SALES) {
      if (s.start && s.start > now) continue;
      if (s.end && s.end <= now) continue;
      if (!(s.percent > best)) continue;
      const inSale = s.mode === 'all' || (s.mode === 'include' ? s.idSet.has(key) : !s.idSet.has(key));
      if (inSale) best = s.percent;
    }
    return best;
  }

  // Price after the discount; Shopify rounds the discount amount to the cent.
  function discountedPrice(price, percent) {
    const cents = Math.round(price * 100);
    return (cents - Math.round(cents * percent / 100)) / 100;
  }

  // Sale: compare-at (or regular) price crossed out, discounted price, "Save N%".
  // JS cards are in USD (Typesense). Liquid cards are in the shopper's currency
  // (Shopify Markets) and carry it in data-currency.
  function moneyFormatter(currency) {
    if (!currency || currency === 'USD') return formatPrice;
    try {
      const nf = new Intl.NumberFormat(undefined, { style: 'currency', currency });
      return p => nf.format(p);
    } catch (e) { return formatPrice; }
  }

  // The data-* attributes (cents, like the Liquid cards) let refreshSalePrices() redo the row.
  function priceRowHTML(id, price, compareAt, currency) {
    const fmt = moneyFormatter(currency);
    const onSale = compareAt > price;
    const pct = salePercent(id);
    const data = `data-price="${Math.round(price * 100)}" data-compare="${onSale ? Math.round(compareAt * 100) : 0}" data-sale="${pct}"`
      + (currency ? ` data-currency="${escapeHtml(currency)}"` : '');
    if (pct > 0) {
      const was = onSale ? compareAt : price;
      const now = discountedPrice(price, pct);
      const save = Math.round((1 - now / was) * 100);
      return `<div class="lts-card__price-row lts-card__price-row--discount" ${data}>
            <span class="lts-card__compare">${fmt(was)}</span>
            <span class="lts-card__price lts-card__price--sale">${fmt(now)}</span>
            <span class="lts-card__save">Save ${save}%</span>
          </div>`;
    }
    const inner = onSale
      ? `<span class="lts-card__compare">${fmt(compareAt)}</span>
         <span class="lts-card__price lts-card__price--sale">${fmt(price)}</span>`
      : `<span class="lts-card__price">${fmt(price)}</span>`;
    return `<div class="lts-card__price-row" ${data}>${inner}</div>`;
  }

  // Redo any card whose percent no longer matches the sale data: the server's
  // fresh copy arrived, or the HTML came from cache and a sale started or ended.
  function refreshSalePrices() {
    if (!el.grid) return;
    el.grid.querySelectorAll('.lts-card[data-product-id] .lts-card__price-row[data-price]').forEach(row => {
      const id = row.closest('.lts-card').getAttribute('data-product-id');
      if (String(salePercent(id)) === String(Number(row.dataset.sale) || 0)) return;
      row.outerHTML = priceRowHTML(id, Number(row.dataset.price) / 100, Number(row.dataset.compare) / 100, row.dataset.currency);
    });
  }

  // Badge tags rendered as bottom-left card overlay (matches live).
  // Order below determines priority (first match wins).
  const SPECIFIC_TAGS = [
    { key: 'best-selling-badge', label: 'Best Seller', modifier: '', img: 'https://cdn.shopify.com/s/files/1/0002/0647/1226/files/heart.png?v=1721673293' },
    { key: 'holiday-staple-badge', label: 'Holiday Staple', modifier: 'holiday-staple', img: 'https://cdn.shopify.com/s/files/1/0002/0647/1226/files/Lola_Gift_Icon_a435f3d8-30c8-43fa-8801-1dccab5f67d4.png?v=1757087441' },
    { key: 'just-in-badge', label: 'Just In', modifier: '', img: 'https://cdn.shopify.com/s/files/1/0002/0647/1226/files/Star.png?v=1721673294' },
    { key: 'personalize-badge', label: 'Personalize it', modifier: 'personalize', img: 'https://cdn.shopify.com/s/files/1/0002/0647/1226/files/Icon_Smile.png?v=1742919149' },
    { key: 'Selling-fast-badge', label: 'Selling Fast', modifier: '', img: 'https://cdn.shopify.com/s/files/1/0002/0647/1226/files/lightning.png?v=1721673293' },
    { key: 'irina-fav-badge', label: "Irina's Favorites", modifier: 'irina-fav', img: 'https://cdn.shopify.com/s/files/1/0002/0647/1226/files/star-tag-img.png?v=1737144658' }
  ];

  function renderSpecificTag(tags) {
    if (!tags || !tags.length) return '';
    for (const t of SPECIFIC_TAGS) {
      if (tags.includes(t.key)) {
        const cls = 'lts-card__tag' + (t.modifier ? ' lts-card__tag--' + t.modifier : '');
        return `<div class="${cls}"><img src="${escapeHtml(t.img)}" alt="" loading="lazy"><span>${escapeHtml(t.label)}</span></div>`;
      }
    }
    return '';
  }

  // Shopify CDN supports on-the-fly resizing via ?width=N AND format
  // conversion via ?format=webp. Grid cards render at ~320px wide on
  // desktop (4-col) and ~180px on mobile (2-col), so 300-700 is plenty.
  // WebP typically saves 30-40% bytes vs JPEG.
  function cdnResize(url, width, format) {
    if (!url) return '';
    if (url.indexOf('cdn.shopify.com') === -1) return url;
    // Strip any existing width/format query and rebuild fresh
    const clean = url
      .replace(/([?&])width=\d+&?/g, '$1')
      .replace(/([?&])format=[a-z]+&?/g, '$1')
      .replace(/[?&]$/, '');
    const sep = clean.indexOf('?') === -1 ? '?' : '&';
    let q = 'width=' + width;
    if (format) q += '&format=' + format;
    return clean + sep + q;
  }

  // srcset tightened to what actually renders (max card = ~350px). Adding
  // widths bigger than the render size wastes bytes without visual gain.
  function buildSrcset(url) {
    if (!url || url.indexOf('cdn.shopify.com') === -1) return '';
    return [240, 360, 480, 640].map(w => cdnResize(url, w, 'webp') + ' ' + w + 'w').join(', ');
  }

  function productCardHTML(doc, opts) {
    opts = opts || {};
    // If this product has color variants, the initial card image should
    // match the FIRST in-stock color (which is also the "active" swatch
    // dot). Otherwise the card shows some product-wide hero shot that
    // doesn't match the visible active dot — user's exact complaint on
    // Bunny Love (Red active dot but hero image showed a different color).
    let firstColor = null;
    if (doc.colors_available) {
      try {
        const arr = JSON.parse(doc.colors_available);
        firstColor = (arr || []).find(c => c && c.in_stock) || null;
      } catch (e) {}
    }
    const img = (firstColor?.image_url) || doc.image || '';
    const hoverImg = (firstColor?.hover_image_url) || doc.image_hover || '';

    const tagHTML = renderSpecificTag(doc.tags);

    const priceHTML = priceRowHTML(doc.id, doc.price, doc.on_sale ? doc.compare_at_price_max : 0);

    // Eager-load the first cards of an appended batch (user is scrolling
     // toward them). Lower-priority cards use lazy loading + async decode.
    const eager = opts.eagerHint === true;
    const priority = eager ? 'eager' : 'lazy';
    const fetchPriority = eager ? 'high' : 'auto';

    const primaryImg = cdnResize(img, 400, 'webp');
    const primarySrcset = buildSrcset(img);
    // Hover image: single 500px WebP, always lazy — only fetched on hover.
    const hoverSrc = hoverImg ? cdnResize(hoverImg, 400, 'webp') : '';
    // Performance: until the page has loaded (or the first tap/scroll/mouse move), hover images and cards after the
    // 8th wait on a placeholder; layout/theme.liquid (lbLoadDeferredImages) fills them in. Cards built after that load normally.
    const later = !window.lbDeferredDone;
    const mainLater = later && opts.index >= 8;
    const LB_GIF = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

    const hoverHTML = hoverImg
      ? `<img class="lts-card__img lts-card__img--hover"
              ${later ? `src="${LB_GIF}" data-lb-src` : 'src'}="${escapeHtml(hoverSrc)}"
              alt="" loading="lazy" decoding="async">`
      : '';

    // Wishlist button — Wishlist King app web component.
    // The app owns the heart icon + active/inactive styling.
    const wishlistHTML = `<wishlist-button-collection
        class="lts-card__wishlist"
        data-product-handle="${escapeHtml(doc.handle)}"
        data-product-id="${doc.id}"
        show-icon
        icon="wishlist"
        align-self="center"
        align-content="center"></wishlist-button-collection>`;

    return `
      <article class="lts-card" role="listitem" data-product-id="${doc.id}">
        <div class="lts-card__image-wrap">
          <a href="${escapeHtml(doc.url)}" class="lts-card__link" title="${escapeHtml(doc.title)}">
            <img class="lts-card__img"
                 ${mainLater ? `src="${LB_GIF}" data-lb-src` : 'src'}="${escapeHtml(primaryImg)}"
                 ${mainLater ? 'data-lb-srcset' : 'srcset'}="${escapeHtml(primarySrcset)}"
                 sizes="(max-width: 480px) 50vw, (max-width: 768px) 33vw, (max-width: 1200px) 25vw, 350px"
                 alt="${escapeHtml(doc.image_alt || doc.title)}"
                 loading="${priority}"
                 fetchpriority="${fetchPriority}"
                 decoding="async"
                 onload="if(!this.hasAttribute('data-lb-src'))this.dataset.loaded='true'"
                 width="${doc.image_width || ''}" height="${doc.image_height || ''}">
            ${hoverHTML}
            ${tagHTML}
          </a>
          ${wishlistHTML}
        </div>
        <div class="lts-card__body">
          ${renderSwatches(doc)}
          <a href="${escapeHtml(doc.url)}" class="lts-card__link">
            <h2 class="lts-card__title">${escapeHtml(doc.title)}</h2>
          </a>
          ${priceHTML}
        </div>
      </article>`;
  }

  // Color swatches — one clickable dot per color that has stock at Lola
  // Showroom. Dot click → /products/HANDLE?variant=ID → PDP auto-selects
  // that variant natively via Shopify. Card image intentionally NOT
  // swapped on hover (image swap is a phase-2 enhancement).
  // Max dots visible on the card. Extras roll into a "+N" indicator
  // that LINKS to the product page (Alo-style — the shopper clicks it
  // to see all colors on the PDP). First dot is marked active with a
  // color-tinted ring so the card reads as "you're viewing this color".
  const MAX_SWATCHES = 5;

  // Detect "too light to be visible on a white card" so we can give
  // those swatches a stronger inset border. Uses simple perceived
  // luminance (Rec. 601). Anything >= ~0.90 gets the strong border.
  function isLightHex(hex) {
    if (!hex || !/^#[0-9a-fA-F]{3,8}$/.test(hex)) return false;
    let h = hex.slice(1);
    if (h.length === 3) h = h.split('').map(c => c + c).join('');
    if (h.length !== 6) return false;
    const r = parseInt(h.slice(0,2), 16) / 255;
    const g = parseInt(h.slice(2,4), 16) / 255;
    const b = parseInt(h.slice(4,6), 16) / 255;
    // Rec. 601 luma
    return (0.299 * r + 0.587 * g + 0.114 * b) >= 0.90;
  }
  function renderSwatches(doc) {
    if (!doc.colors_available) return '';
    let arr;
    try { arr = JSON.parse(doc.colors_available); } catch (e) { return ''; }
    if (!Array.isArray(arr) || !arr.length) return '';
    // Only colors currently in stock at Showroom.
    const inStock = arr.filter(c => c && c.in_stock);
    if (!inStock.length) return '';
    const shown = inStock.slice(0, MAX_SWATCHES);
    const overflow = inStock.length - shown.length;
    const dots = shown.map((c, i) => {
      const hex = c.hex && /^#[0-9a-fA-F]{3,8}$/.test(c.hex) ? c.hex : '';
      const styleBg = hex ? `background:${hex}` : '';
      const noHexCls = hex ? '' : ' lts-card__swatch--nohex';
      const lightCls = isLightHex(hex) ? ' lts-card__swatch--light' : '';
      const href = `${escapeHtml(doc.url)}?variant=${encodeURIComponent(c.variant_id)}`;
      const isActive = i === 0;
      const activeCls = isActive ? ' lts-card__swatch--active' : '';
      // Ring color: swatch's own hex, except when the swatch is a
      // "too light to see on white" one — then dark grey so the ring
      // stays visible on the card background.
      const glowHex = isLightHex(hex) ? '#333333' : (hex || '#8a8a8a');
      const glow = isActive ? `;--lts-swatch-glow:${glowHex}` : '';
      const dataImg = c.image_url ? cdnResize(c.image_url, 400, 'webp') : '';
      const dataImgAttr = dataImg ? ` data-image="${escapeHtml(dataImg)}"` : '';
      const dataHover = c.hover_image_url ? cdnResize(c.hover_image_url, 400, 'webp') : '';
      const dataHoverAttr = dataHover ? ` data-hover-image="${escapeHtml(dataHover)}"` : '';
      // data-hex = the source-of-truth for the swatch's own color. The
      // click handler reads this to set --lts-swatch-glow reliably; we
      // can't parse it out of style.background because browsers
      // normalize `background:#FFC0CB` to `rgb(255,192,203)` on read,
      // and the previous regex-hunt for `#hex` in that string missed
      // → --lts-swatch-glow stayed unset → CSS fallback (dark grey)
      // kicked in → user saw a "black" ring on click.
      const dataHex = hex ? ` data-hex="${hex}"` : '';
      const styleAttr = (styleBg || glow) ? ` style="${styleBg}${glow}"` : '';
      return `<a class="lts-card__swatch${activeCls}${noHexCls}${lightCls}" href="${href}"
                  aria-label="${escapeHtml(c.name)}"
                  title="${escapeHtml(c.name)}"
                  data-color-name="${escapeHtml(c.name)}"${dataImgAttr}${dataHoverAttr}${dataHex}${styleAttr}></a>`;
    }).join('');
    // The "+N" is a real link to the PDP so clicking it opens the
    // product page (where the shopper can see all colors laid out).
    const more = overflow > 0
      ? `<a class="lts-card__swatch-more" href="${escapeHtml(doc.url)}" aria-label="View all ${inStock.length} colors on the product page">+${overflow}</a>`
      : '';
    return `<div class="lts-card__swatches" role="group" aria-label="Available colors">${dots}${more}</div>`;
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function renderResults(res, append) {
    let hits = res.hits || [];

    // Dedupe against already-rendered cards. Shopify's SSR collection order and
    // /products.json order can diverge (a product can be at pos 4 in SSR but
    // pos 41 in the JSON API), so appended pages sometimes contain products
    // already in the grid. Skip any hit whose product id is already in the DOM.
    if (append && hits.length && el.grid) {
      const existing = new Set(
        [...el.grid.querySelectorAll('.lts-card[data-product-id]')]
          .map(c => c.getAttribute('data-product-id'))
      );
      hits = hits.filter(h => !existing.has(String(h.document.id)));
    }

    // Preload first-batch images IMMEDIATELY — before parsing HTML — so the
    // browser starts fetching in parallel with DOM construction. Cuts perceived
    // load time by 200-500ms on filter clicks where images aren't cached.
    if (hits.length) {
      // Performance: first results page preloads only the first 4 pictures (what a phone shows); more competed with them.
      preloadImages(hits.slice(0, append ? 12 : 4).map(h => h.document.image), 'high');
    }

    if (!append) el.grid.innerHTML = '';

    if (!hits.length && !append) {
      el.empty.hidden = false;
    } else {
      el.empty.hidden = true;
      // Keep filtered/reset results snappy: prioritize the first visible cards.
      // Appended batches are already prefetched, so every appended image can
      // paint eagerly from cache as the user scrolls toward it.
      const html = hits.map((h, index) =>
        productCardHTML(h.document, { eagerHint: append || index < 4, index: append ? 0 : index })
      ).join('');
      el.grid.insertAdjacentHTML('beforeend', html);
    }

    // Count — only overwrite the authoritative total when a wider query
    // gave us the real collection-wide number (state.total is trusted;
    // page-scoped merchant-order responses only reflect their own batch).
    if (!state.usingMerchantOrder || state.page === 1 || !state.total) {
      state.total = res.found;
    }
    if (el.count) el.count.textContent = (state.total || res.found).toLocaleString() + ' STYLES';

    // Facets — re-render on every response that carries facet_counts.
    // Two situations otherwise silently break:
    //  1. First response = merchant-order lookup with empty facets → locked
    //     empty forever if we init on that.
    //  2. Fallback-tag or filter-scoped response arriving later would be
    //     ignored → filters never appear.
    // renderFacets is pure current-state ∪ selected, so re-running is safe.
    if (res.facet_counts && res.facet_counts.some(f => f.counts && f.counts.length)) {
      renderFacets(res.facet_counts);
      state.facetsInitialized = true;
    }

    // Exhaustion tracking only — no visible end-of-list message.
    const inGrid = el.grid.querySelectorAll('.lts-card:not(.lts-card--skeleton)').length;
    const total = state.total || res.found;
    state.exhausted = (total > 0 && inGrid >= total) || hits.length === 0;
  }

  // Renders sizes (whitelist, in fixed order) and categories (fixed 14 in
  // two groups). Categories stay visible so the header remains pixel-stable.
  function renderFacets(facetCounts) {
    // We facet on `sizes_available` (only currently in-stock sizes) so sold-out
    // sizes never appear as clickable pills for a collection.
    const sizesFacet = facetCounts.find(f => f.field_name === 'sizes_available')
      || facetCounts.find(f => f.field_name === 'sizes');
    const typesFacet = facetCounts.find(f => f.field_name === 'product_type');

    if (el.sizeRow) renderSizes(el.sizeRow, sizesFacet ? sizesFacet.counts : []);
    if (el.sizeRowMobile) renderSizes(el.sizeRowMobile, sizesFacet ? sizesFacet.counts : []);
    renderCategories(typesFacet ? typesFacet.counts : []);
  }

  function renderSizes(target, counts) {
    // Freeze the collection's UNFILTERED size assortment on first paint,
    // then reuse forever. If we based the button-vs-link decision on the
    // current (narrowed) facet counts, picking one filter would strip
    // every other size to a shop-all link — the shopper's next click
    // would jump off the current collection. state.availableSizes stays
    // stable so multi-select on the collection works correctly.
    const present = new Set();
    counts.forEach(c => { const canon = canonicalSize(c.value); if (canon) present.add(canon); });
    // Only cache the baseline when NO user filters are active — otherwise
    // the response is narrowed and would freeze an incomplete assortment.
    const noUserFilters = state.selected.sizes.size === 0 && state.selected.product_type.size === 0;
    if (!state.availableSizes && present.size && noUserFilters) state.availableSizes = new Set(present);
    const inCollection = state.availableSizes || present;

    // Two behaviors:
    //  - shop-all page: EVERY size is a clickable multi-select button.
    //  - Any other collection: sizes present in the collection are
    //    buttons; missing sizes are <a> links to shop-all.
    const onShopAll = CFG.collectionHandle === 'shop-all';

    target.innerHTML = ALLOWED_SIZES.map(size => {
      const active = state.selected.sizes.has(size);
      const hasProducts = inCollection.has(size) || active;
      if (onShopAll || hasProducts) {
        return `
          <button type="button"
                  class="lts-pill${active ? ' is-active' : ''}"
                  data-value="${escapeHtml(size)}"
                  aria-pressed="${active}">
            ${escapeHtml(size)}
          </button>`;
      }
      // Not in this collection → shop-all fallback (with this size applied).
      const href = `/collections/shop-all?sizes=${encodeURIComponent(size)}`;
      return `
        <a class="lts-pill lts-pill--linked" href="${href}" data-lts-link>
          ${escapeHtml(size)}
        </a>`;
    }).join('');
  }

  function renderCategories(counts) {
    // Sum the counts from Typesense product_type facet into our fixed groups.
    const totals = {};
    for (const cat of [...CATEGORIES.kids, ...CATEGORIES.adults]) {
      totals[cat.key] = 0;
      for (const c of counts) {
        const v = String(c.value || '').toLowerCase();
        if (cat.matches.some(m => v.includes(m))) totals[cat.key] += c.count;
      }
      // Sale category: always considered present so the button shows on every
      // collection. Actual count is derived from the on_sale facet if we
      // requested it — for now just mark as present.
      if (cat.isSale) totals[cat.key] = 1;
    }

    // Freeze the collection's UNFILTERED category assortment on first
    // paint (same reason as renderSizes): picking one category narrows
    // the response's facet_counts to just that subset; without the
    // baseline every other category becomes a shop-all link and the
    // next click jumps off the current collection.
    const present = new Set(Object.keys(totals).filter(k => totals[k] > 0));
    // Only cache when NO user filters are active (otherwise the facet
    // response is narrowed to the selected subset and would freeze an
    // incomplete assortment).
    const noUserFilters = state.selected.sizes.size === 0 && state.selected.product_type.size === 0;
    if (!state.availableCategories && noUserFilters) {
      const realCatsPresent = [...present].some(k => k !== 'sale');
      if (realCatsPresent) state.availableCategories = new Set(present);
    }
    const inCollection = state.availableCategories || present;
    const onShopAll = CFG.collectionHandle === 'shop-all';

    const renderGroup = (list) => list.map(cat => {
        const active = state.selected.product_type.has(cat.key);
        const hasProducts = cat.isSale || inCollection.has(cat.key) || active;
        // Per-icon-scoped class prefix — inline <style> in SVG cascades
        // globally, so unscoped `.cls-1` from one icon stomps another's fill.
        const rawIcon = CATEGORY_ICONS[cat.key] || FALLBACK_SVG;
        const iconHtml = rawIcon
          .replace(/\.cls-(\d+)/g, `.${cat.key}-cls-$1`)
          .replace(/class="cls-(\d+)"/g, `class="${cat.key}-cls-$1"`);
        const iconLabel = `
            <span class="lts-cat-icon">${iconHtml}</span>
            <span class="lts-cat-label">${escapeHtml(cat.label)}</span>`;
        if (onShopAll || hasProducts) {
          return `
          <button type="button"
                  class="lts-cat${active ? ' is-active' : ''}"
                  data-value="${escapeHtml(cat.key)}"
                  aria-pressed="${active}">${iconLabel}
          </button>`;
        }
        const href = `/collections/shop-all?types=${encodeURIComponent(cat.key)}`;
        return `
        <a class="lts-cat lts-cat--linked" href="${href}" data-lts-link>${iconLabel}
        </a>`;
      }).join('');

    // Single flat group — all categories in one row (no kids/adults split).
    const allHtml = renderGroup([...CATEGORIES.kids, ...CATEGORIES.adults]);
    const all = el.root.querySelector('[data-lts-cat-group="all"]');
    if (all) all.innerHTML = allHtml;
    if (el.catAllMobile) el.catAllMobile.innerHTML = allHtml;
  }

  function renderActiveChips() {
    if (!el.activeChips) return;
    const chips = [];
    const allCats = [...CATEGORIES.kids, ...CATEGORIES.adults];
    state.selected.sizes.forEach(v => chips.push({ facet: 'sizes', value: v, label: canonicalSize(v) || v }));
    state.selected.product_type.forEach(key => {
      const cat = allCats.find(c => c.key === key);
      chips.push({ facet: 'product_type', value: key, label: cat ? cat.label : key });
    });

    if (!chips.length) { el.activeChips.hidden = true; el.activeChips.innerHTML = ''; return; }

    el.activeChips.hidden = false;
    el.activeChips.innerHTML = chips.map(c =>
      `<button type="button" class="lts-chip" data-facet="${c.facet}" data-value="${escapeHtml(c.value)}" aria-label="Remove ${escapeHtml(c.label)} filter">
         ${escapeHtml(c.label)}<span class="lts-chip-x">×</span>
       </button>`
    ).join('') + `<button type="button" class="lts-clear-all-inline" data-lts-clear-all>Clear all</button>`;
  }

  // ---------- Actions ----------
  async function load(reset) {
    // On reset (filter/sort/clear) we STOP any in-flight request and start
    // fresh — this prevents an old query from clobbering the newer filter
    // state. Non-reset (infinite scroll) still queues gracefully.
    if (reset && state.loading) {
      state.loadSeq = (state.loadSeq || 0) + 1;
      state.loading = false;
    } else if (state.loading) {
      return;
    }

    if (reset) {
      state.page = 1;
      state.exhausted = false;
      state.facetsInitialized = false;
      state.prefetchedPage = null;
      state.listGen++;
      // Any filter/sort/facet change invalidates the merchant-order cache —
      // the cached filtered-ID sequence is only valid for one filter set.
      state.filteredOrderCache = null;
    } else {
      if (state.exhausted) return;
      state.page++;
    }

    // Each load() gets a monotonically-increasing sequence number. When the
    // response comes back we compare — if a newer load() has started, we
    // silently drop this response instead of overwriting fresh state.
    state.loadSeq = (state.loadSeq || 0) + 1;
    const seq = state.loadSeq;

    state.loading = true;
    // Show the spinner only if we truly need to wait for network. When the
    // next page was already prefetched, we skip the loader entirely.
    const havePrefetched = !reset
      && state.prefetchedPage
      && state.prefetchedPage.page === state.page;
    if (!havePrefetched && el.loader) el.loader.hidden = false;

    try {
      let res;
      if (havePrefetched) {
        res = state.prefetchedPage.data;
        state.prefetchedPage = null;
      } else {
        res = await search(state.page);
      }

      // Guard: if a newer load fired while we were awaiting, drop this result.
      if (seq !== state.loadSeq) return;

      renderResults(res, !reset);
      renderActiveChips();
      writeUrlState();

      // Chain-prefetch: next page in the background so scroll feels instant.
      if (!state.exhausted) prefetchNext();
    } catch (e) {
      if (seq !== state.loadSeq) return;  // stale error, ignore
      console.error('[LATB]', e);
      const visibleCards = el.grid
        ? el.grid.querySelectorAll('.lts-card:not(.lts-card--skeleton)').length
        : 0;
      const fallbackTotal = state.total || Number(CFG.initialCount) || visibleCards || 0;
      if (el.count) el.count.textContent = fallbackTotal.toLocaleString() + ' STYLES';
      if (reset && el.grid && !visibleCards) el.grid.innerHTML = '';
    } finally {
      if (seq === state.loadSeq) {
        state.loading = false;
        if (el.loader) el.loader.hidden = true;
      }
    }
  }

  // Inject high-priority image preloads into the document head so the browser
  // starts fetching NEXT-page images at scanner speed — before we even render
  // their <img> tags. Uses a single <link rel="preload" as="image"> per URL.
  // Performance: the preload offers the card's own srcset + sizes and no crossorigin, so the browser picks the very
  // file the card <img> will use and the card reuses it. Before, it preloaded a 400px CORS copy that no card ever
  // used (cards pick 240-640px from their srcset), so every preloaded picture was downloaded twice.
  const CARD_IMG_SIZES = '(max-width: 480px) 50vw, (max-width: 768px) 33vw, (max-width: 1200px) 25vw, 350px';
  const preloadedSrcsets = new Set();
  function preloadImages(sourceUrls, priority) {
    const head = document.head;
    sourceUrls.forEach(src => {
      const srcset = buildSrcset(src);
      if (!srcset || preloadedSrcsets.has(srcset)) return;
      preloadedSrcsets.add(srcset);
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.imageSrcset = srcset;
      link.imageSizes = CARD_IMG_SIZES;
      link.fetchPriority = priority || 'auto';
      head.appendChild(link);
    });
  }

  function prefetchNext() {
    if (state.prefetchedPage || state.exhausted) return;
    const nextPage = state.page + 1;
    const gen = state.listGen;
    search(nextPage)
      .then(data => {
        // The shopper changed sort or filters while this was in flight: it
        // is a page of the OLD list and must not be appended to the new one.
        if (gen !== state.listGen) return;
        state.prefetchedPage = { page: nextPage, data };
        // Preload ALL of the next page's images so they're already in the
        // browser cache when the batch renders on scroll. Low priority: they
        // are for later and must not slow what is on screen now.
        const hits = data && data.hits ? data.hits : [];
        const preloadNext = () => preloadImages(hits.map(h => h.document.image), 'low');
        // Wait until the first screen is done (lbLoadDeferredImages in layout/theme.liquid).
        if (window.lbDeferredDone) preloadNext();
        else window.addEventListener('lb:deferred-loaded', preloadNext, { once: true });
      })
      .catch(() => { /* silent — will retry on demand */ });
  }

  // Skeleton HTML for the reset/filter transition — same aspect ratio as
  // real cards so no layout shift when the real data lands.
  function skeletonCardHTML() {
    return `<div class="lts-card lts-card--skeleton" aria-hidden="true">
      <div class="lts-card__image-wrap lts-skeleton"></div>
      <div class="lts-card__body">
        <div class="lts-skeleton" style="height:14px;width:80%;margin-bottom:6px"></div>
        <div class="lts-skeleton" style="height:12px;width:40%"></div>
      </div>
    </div>`;
  }

  function showSkeleton(count) {
    if (!el.grid) return;
    el.grid.innerHTML = new Array(count || 12).fill(skeletonCardHTML()).join('');
  }

  function toggleFacet(facet, value) {
    const s = state.selected[facet];
    const nowActive = !s.has(value);
    if (nowActive) s.add(value); else s.delete(value);

    // Optimistic UI — flip the pill/category active class BEFORE the network
    // request fires. Facet re-render after the search response confirms it.
    const cls = facet === 'sizes' ? '.lts-pill' : '.lts-cat';
    document.querySelectorAll(cls + '[data-value="' + CSS.escape(value) + '"]').forEach(el => {
      el.classList.toggle('is-active', nowActive);
      el.setAttribute('aria-pressed', nowActive ? 'true' : 'false');
    });

    // Instant visual response: show skeleton grid while the new query runs.
    showSkeleton(12);
    load(true);
  }

  function clearAll() {
    state.selected.sizes.clear();
    state.selected.product_type.clear();
    // Optimistic UI — instantly strip active state from all filter chips,
    // pills and categories BEFORE the Typesense request starts so the user
    // sees the filters clear the moment they click, not after the network.
    document.querySelectorAll('.lts-pill.is-active, .lts-cat.is-active').forEach(el => {
      el.classList.remove('is-active');
      el.setAttribute('aria-pressed', 'false');
    });
    if (el.activeChips) { el.activeChips.hidden = true; el.activeChips.innerHTML = ''; }
    showSkeleton(12);
    load(true);
  }

  // ---------- Event wiring ----------
  function wire() {
    el.sorts.forEach(sort => {
      sort.addEventListener('change', () => {
        state.sort = sort.value;
        syncSortDisplay();
        showSkeleton(12);
        load(true);
      });
      // Opening the sort menu is the earliest hint that BEST SELLING may be picked.
      ['pointerdown', 'focus'].forEach(evt => sort.addEventListener(evt, prewarmBestSelling, { passive: true }));
    });

    // Card color swatch UX:
    //  • HOVER swatch → NO color-image swap. The generic hover-alt
    //    product image still shows via the :has() CSS rule (same as
    //    hovering the image itself). Hover just quietly preloads the
    //    swatch's color image so the CLICK swap is instant.
    //  • CLICK swatch → swap card's main image to that color's image,
    //    mark that dot active (colored ring), DO NOT navigate. Card
    //    title/image link now carries ?variant=ID so a later click on
    //    the title opens PDP on that color.
    if (el.grid) {
      const preloadCache = new Set();
      const preload = (url) => {
        if (!url || preloadCache.has(url)) return;
        preloadCache.add(url);
        const img = new Image(); img.decoding = 'async'; img.loading = 'eager'; img.src = url;
      };
      const setActive = (s, card) => {
        card.querySelectorAll('.lts-card__swatch--active').forEach(a => {
          a.classList.remove('lts-card__swatch--active');
          a.style.removeProperty('--lts-swatch-glow');
        });
        s.classList.add('lts-card__swatch--active');
        // Prefer data-hex (source of truth). Fall back to parsing
        // computed background color when the swatch has no known hex.
        const hex = s.dataset.hex
          || (getComputedStyle(s).backgroundColor || '').match(/#[0-9a-fA-F]{3,8}/)?.[0]
          || '#8a8a8a';
        // If the swatch color itself is too light to be visible as a
        // ring on a white card (white, cream, pale pink…), swap the
        // glow to a dark grey so the active state is unmistakable.
        const glow = s.classList.contains('lts-card__swatch--light') ? '#333333' : hex;
        s.style.setProperty('--lts-swatch-glow', glow);
        const vid = s.getAttribute('href')?.split('variant=')[1];
        if (vid) {
          card.querySelectorAll('a.lts-card__link').forEach(a => {
            const base = (a.getAttribute('href') || '').split('?')[0];
            a.setAttribute('href', `${base}?variant=${vid}`);
          });
        }
      };

      // Hover — preload only (no image swap here).
      el.grid.addEventListener('pointerover', e => {
        const s = e.target.closest && e.target.closest('.lts-card__swatch[data-image]');
        if (s) preload(s.dataset.image);
      });
      // Click — ANY swatch: always prevent navigation. If the swatch
      // carries a color-specific image, swap the card image + hover
      // image; otherwise just move the active ring without swapping.
      // Match: entire .lts-card__swatch class (not just [data-image])
      // so colors without an image URL don't accidentally navigate.
      el.grid.addEventListener('click', e => {
        const s = e.target.closest && e.target.closest('.lts-card__swatch');
        if (!s) return;
        e.preventDefault();
        e.stopPropagation();
        const url = s.dataset.image || '';
        const card = s.closest('.lts-card');
        if (!card) return;
        // Only swap image if this swatch actually carries one. Colors
        // without a variant image just move the active ring — no
        // navigation, no broken image swap.
        if (url) {
          const img = card.querySelector('.lts-card__img:not(.lts-card__img--hover)');
          if (img) {
            img.removeAttribute('srcset');
            img.src = url;
            img.dataset.loaded = 'true';
          }
          const hoverImg = card.querySelector('.lts-card__img--hover');
          const hoverUrl = s.dataset.hoverImage || '';
          if (hoverImg) {
            if (hoverUrl && hoverUrl !== url) {
              hoverImg.removeAttribute('srcset');
              hoverImg.src = hoverUrl;
              hoverImg.style.removeProperty('display');
            } else {
              hoverImg.style.display = 'none';
            }
          }
        }
        setActive(s, card);
      });
    }

    // Delegate size + category clicks from both desktop and mobile mirrors.
    // Skip when the target is a shop-all link (`<a data-lts-link>`) — let
    // the browser navigate; also skip disabled buttons (unavailable state).
    root.addEventListener('click', e => {
      if (e.target.closest('[data-lts-link]')) return;
      const pill = e.target.closest('.lts-pill');
      if (pill && pill.tagName === 'BUTTON' && !pill.disabled) {
        toggleFacet('sizes', pill.dataset.value); return;
      }
      const cat = e.target.closest('.lts-cat');
      if (cat && cat.tagName === 'BUTTON' && !cat.disabled) {
        toggleFacet('product_type', cat.dataset.value); return;
      }
    });

    // Drawer toggle
    let drawerScrollY = 0;

    function openDrawer() {
      if (!el.drawer || !el.drawer.hidden) return;
      drawerScrollY = window.scrollY || window.pageYOffset || 0;
      el.drawer.hidden = false;
      el.drawer.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('lts-drawer-open');
      document.body.classList.add('lts-drawer-open');
      document.body.style.position = 'fixed';
      document.body.style.top = `-${drawerScrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    }

    function closeDrawer() {
      if (!el.drawer || el.drawer.hidden) return;
      el.drawer.hidden = true;
      el.drawer.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('lts-drawer-open');
      document.body.classList.remove('lts-drawer-open');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      window.scrollTo(0, drawerScrollY);
    }

    if (el.drawerOpenBtn) el.drawerOpenBtn.addEventListener('click', openDrawer);
    el.drawerCloseBtns.forEach(b => {
      b.addEventListener('pointerdown', e => {
        e.preventDefault();
        e.stopPropagation();
        closeDrawer();
      });
      b.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        closeDrawer();
      });
    });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && el.drawer && !el.drawer.hidden) closeDrawer();
    });

    if (el.activeChips) {
      el.activeChips.addEventListener('click', e => {
        const chip = e.target.closest('.lts-chip');
        if (chip) {
          e.preventDefault();
          e.stopPropagation();
          toggleFacet(chip.dataset.facet, chip.dataset.value);
          return;
        }
        const clearBtn = e.target.closest('[data-lts-clear-all]');
        if (clearBtn) {
          e.preventDefault();
          e.stopPropagation();
          clearAll();
        }
      });
    }

    // Delegated Clear-All handler — catches every [data-lts-clear-all] click
    // in the whole section (empty-state button, drawer footer, active-chips
    // inline link, etc.). The prior activeChips-scoped listener stays for
    // event.stopPropagation on chip clicks, but this delegated one is what
    // actually resets state.
    root.addEventListener('click', e => {
      const btn = e.target.closest('[data-lts-clear-all]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      clearAll();
    });

    if (el.sentinel && 'IntersectionObserver' in window) {
      // Trigger load well before the sentinel scrolls into view. 3000px ≈
      // 3–4 viewport heights ahead — combined with prefetchNext(), pagination
      // appends instantly (data is already in memory when append happens).
      const io = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && !state.loading && !state.exhausted) load(false);
      }, { rootMargin: '3000px 0px' });

      io.observe(el.sentinel);

      // Backup: scroll-position check. Fires when the user is within 2
      // viewport heights of the bottom of the document — catches cases where
      // IntersectionObserver hasn't fired yet (fast scroll / short pages).
      let ticking = false;
      window.addEventListener('scroll', () => {
        if (ticking || state.loading || state.exhausted) return;
        ticking = true;
        requestAnimationFrame(() => {
          const scrollBottom = window.scrollY + window.innerHeight;
          const threshold = document.documentElement.scrollHeight - (window.innerHeight * 2);
          if (scrollBottom >= threshold) load(false);
          ticking = false;
        });
      }, { passive: true });
    }
  }

  // ---------- Analytics (lightweight, non-blocking) ----------
  // Batches events client-side and ships them via navigator.sendBeacon (or
  // fetch fallback) to https://167-233-171-51.sslip.io/analytics/track.
  // Tracks: view · scroll milestones · card clicks · filter/sort/search.
  const analytics = (() => {
    const ENDPOINT = 'https://167-233-171-51.sslip.io/analytics/track';
    const SCROLL_MILESTONES = [12, 25, 40, 60, 80, 100];
    const seenScroll = new Set();
    let sessionId;
    try {
      sessionId = sessionStorage.getItem('lts_sid');
      if (!sessionId) {
        sessionId = 's_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
        sessionStorage.setItem('lts_sid', sessionId);
      }
    } catch { sessionId = 's_' + Math.random().toString(36).slice(2, 10); }

    const queue = [];
    let flushTimer = null;

    function collection() {
      return CFG.isSearchPage ? '__search__' : (CFG.collectionHandle || null);
    }

    function flush() {
      if (queue.length === 0) return;
      const payload = JSON.stringify({ session: sessionId, events: queue.splice(0) });
      try {
        if (navigator.sendBeacon) {
          // Use text/plain so browser skips CORS preflight (sendBeacon with
          // application/json triggers OPTIONS which our origin blocks).
          navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: 'text/plain' }));
        } else {
          fetch(ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'text/plain' },
            body: payload,
            keepalive: true,
            mode: 'cors'
          }).catch(() => {});
        }
      } catch { /* silent */ }
    }

    function track(type, data) {
      queue.push({ type, ts: Date.now(), collection: collection(), data: data || {} });
      clearTimeout(flushTimer);
      flushTimer = setTimeout(flush, 800);
      if (queue.length >= 20) flush();
    }

    // Flush on page hide (guarantees delivery)
    window.addEventListener('pagehide', flush);
    window.addEventListener('beforeunload', flush);
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flush(); });

    // Scroll depth
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const height = document.documentElement.scrollHeight - window.innerHeight;
        if (height > 0) {
          const p = Math.round(((window.scrollY + window.innerHeight) / document.documentElement.scrollHeight) * 100);
          for (const m of SCROLL_MILESTONES) {
            if (p >= m && !seenScroll.has(m)) {
              seenScroll.add(m);
              const productsShown = el.grid ? el.grid.querySelectorAll('.lts-card:not(.lts-card--skeleton)').length : 0;
              track('scroll', { pct: m, products_shown: productsShown });
            }
          }
        }
        ticking = false;
      });
    }, { passive: true });

    // Card clicks (delegated)
    document.addEventListener('click', e => {
      const card = e.target.closest('.lts-card:not(.lts-card--skeleton)');
      if (!card) return;
      const link = e.target.closest('a[href*="/products/"]');
      if (!link) return;
      const productId = card.getAttribute('data-product-id');
      const title = card.querySelector('.lts-card__title')?.textContent?.trim();
      const href = link.getAttribute('href') || '';
      const handleMatch = href.match(/\/products\/([^/?#]+)/);
      const position = Array.from(card.parentElement?.children || []).indexOf(card);
      track('click', {
        product_id: productId,
        title: title,
        handle: handleMatch ? handleMatch[1] : null,
        position: position
      });
    }, true);

    return {
      trackView() {
        track('view', {
          title: CFG.collectionTitle,
          is_search: !!CFG.isSearchPage,
          initial_count: CFG.initialCount || 0
        });
      },
      trackFilter(facet, value, action) {
        track('filter', { facet, value, action: action || 'toggle' });
      },
      trackSort(sortValue) {
        track('sort', { sort: sortValue });
      },
      trackSearch(query) {
        if (query) track('search', { q: String(query).slice(0, 100) });
      }
    };
  })();

  // ---------- Boot ----------
  readUrlState();
  wire();

  // Analytics — hooked via CAPTURING click listener on root, before other
  // handlers. Just records the event; the actual filter/sort logic is
  // untouched so existing behavior stays intact.
  root.addEventListener('click', e => {
    if (e.target.closest('[data-lts-link]')) {
      // Shop-all fallback link — track the outbound filter and let navigation happen.
      const linked = e.target.closest('.lts-pill,.lts-cat');
      if (linked) {
        const facet = linked.classList.contains('lts-pill') ? 'sizes' : 'product_type';
        const val = (linked.getAttribute('href') || '').split('=').pop() || '';
        analytics.trackFilter(facet, decodeURIComponent(val), 'shop-all-fallback');
      }
      return;
    }
    const pill = e.target.closest('.lts-pill');
    if (pill && pill.tagName === 'BUTTON' && !pill.disabled) {
      const val = pill.dataset.value;
      const wasSelected = state.selected.sizes.has(val);
      analytics.trackFilter('sizes', val, wasSelected ? 'remove' : 'add');
      return;
    }
    const cat = e.target.closest('.lts-cat');
    if (cat && cat.tagName === 'BUTTON' && !cat.disabled) {
      const val = cat.dataset.value;
      const wasSelected = state.selected.product_type.has(val);
      analytics.trackFilter('product_type', val, wasSelected ? 'remove' : 'add');
    }
  }, true);

  if (el.sort) {
    el.sort.addEventListener('change', () => analytics.trackSort(el.sort.value));
  }
  analytics.trackView();
  if (CFG.isSearchPage && state.query) analytics.trackSearch(state.query);
  if (el.count) el.count.textContent = state.total.toLocaleString() + ' STYLES';
  // Note: don't preemptively renderFacets([]) — that would freeze an empty
  // baseline and permanently hide the size/category rows. The real facet
  // response arrives via fetchFacetsAndTotal() a moment later.

  // If the server already rendered the first page inline (SSR path), skip
  // the initial fetch — just kick off a background facets/count request so
  // the UI has real numbers, and prefetch the next page for scroll.
  // BUT: if the URL had pre-selected filters (?sizes= / ?types= / non-default
  // sort), we can't trust the SSR grid — it's the unfiltered page 1 — so we
  // force a fresh filtered fetch to align the grid with URL state.
  // The server's copy of the sale data wins over CFG.sales (Shopify page cache).
  if (window.LolaSales) window.LolaSales.load().then(list => { setSales(list); refreshSalePrices(); });

  const hasUrlFilters = state.selected.sizes.size > 0
    || state.selected.product_type.size > 0
    || state.sort !== DEFAULT_SORT;

  const ssrPresent = !hasUrlFilters
    && el.grid && el.grid.hasAttribute('data-lts-ssr')
    && el.grid.querySelector('.lts-card:not(.lts-card--skeleton)');

  if (ssrPresent) {
    // SSR already rendered page 1 in the HTML. Mark state accordingly so
    // the next scroll triggers page 2, not a duplicate page 1.
    state.page = 1;
    state.usingMerchantOrder = true;
    state.exhausted = false;

    state.ssrIds = new Set([...el.grid.querySelectorAll('.lts-card[data-product-id]')]
      .map(c => c.getAttribute('data-product-id')));

    // Give page-1 SSR cards their swatches (Liquid can't render them).
    hydrateSsrSwatches();
    refreshSalePrices();

    // Kick off prefetch IMMEDIATELY — don't wait for scroll. This gets page
    // 2 and its images into cache while the user is still reading page 1.
    prefetchNext();

    // Preload Shopify merchant-order IDs in the background so the very first
    // filter click doesn't have to wait for the (slow) collection JSON call.
    // Performance: start after the first screen is done (or at the first tap,
    // e.g. on a filter) — up to 6 x 250-product JSON pages competed with the
    // banner and first cards. A filter click before that fetches on demand.
    if (CFG.collectionHandle) {
      const preloadOrder = () => preloadMerchantIds(CFG.collectionHandle);
      if (window.lbDeferredDone) preloadOrder();
      else window.addEventListener('lb:deferred-loaded', preloadOrder, { once: true });
    }

    // Two-tier facet lookup for SSR pages:
    //  1. fetchFacetsAndTotal (collection_handle → tag+type fallback) — cheap.
    //  2. If both scopes returned 0, use Shopify's merchant order IDs and
    //     query TS by id. Handles collections like `mommy-me-1` where TS
    //     docs don't carry the handle in `collection_handles` and the
    //     smart-fallback tag/type pair (`Mommy`+`me`) doesn't match either.
    (async () => {
      let res = await fetchFacetsAndTotal().catch(() => null);
      const empty = !res || !res.found;
      if (empty && CFG.collectionHandle) {
        const ids = await fetchMerchantOrderIds(CFG.collectionHandle, 1, false).catch(() => null);
        if (ids && ids.length) {
          const idRes = await typesenseByIds(ids, 1, { facetOnly: true, skipUserFilters: true }).catch(() => null);
          if (idRes && idRes.found) res = idRes;
        }
      }
      if (!res) return;
      if (res.facet_counts) { renderFacets(res.facet_counts); state.facetsInitialized = true; }
      // Shopify's SSR-provided total is authoritative for the collection —
      // any Typesense-scoped result may be smaller (id-fallback caps at
      // ~250, tag+type may miss custom-curated products). Prefer it.
      state.total = Number(CFG.initialCount) || res.found || 0;
      if (state.total && el.count) el.count.textContent = state.total.toLocaleString() + ' STYLES';
      const inGrid = el.grid.querySelectorAll('.lts-card:not(.lts-card--skeleton)').length;
      state.exhausted = state.total > 0 && inGrid >= state.total;
    })();
  } else {
    if (CFG.collectionHandle) preloadMerchantIds(CFG.collectionHandle);
    load(true);
  }
})();
